// kernel/useChat.ts
import { MAIN_TEXT_MODEL } from '#lib/gptBackend';
import { useCallback, RefObject, useMemo, useRef, createElement, type ReactElement } from 'react';
import { resolveProjectStudioSection, restoreProjectToolView } from '#lib/studio-project-view';
import { sourceActionFailure } from '#lib/source-actions';
import { launchTypedSlidesReview, useSlidesStore } from '@/components/slides/slidesStore';
import { serializeListOutputMentions } from '#lib/list/listOutputMentions';
import { serializeFactoryFolderMentions } from '#lib/list/factoryFolderMentions';
import { requiresListScriptPersistence } from '#lib/list/factoryReferenceLookup';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import {
  ActiveEntityFocus,
  SessionCreationScope,
  ChatSubjectFocusEntry,
  ChatSession, 
  ChatSessionInventoryEntityState,
  CollaborationMode,
  ImageAttachment, 
  KernelHandle, 
  Message, 
  LockableFile,
  useChatReturnType,
  DEFAULT_GLOBAL_SESSION_NAME,
  ContextAnchor,
  EntityType,
  SessionTableFocusView,
  WorkingMemory,
  Setter,
  LeasingActionItem,
  TimelineEventType,
  TimelineEventDTO,
  AgentMode,
  AGENT_LLMS,
  QuickMenuAction,
  ChatComposerSubmitOptions,
  ChatComposerQueueOptions,
  ChatComposerAttachMenuOptions,
  PromptLibraryListParams,
  PromptLibraryListResponse,
  ChatStatusBrand,
} from '#types/types';
import { ImagePlus, Layers, Paperclip, Trash2 } from '@/components/ui/lucide-memo';
import {
  getGenericImageInputContextMenuActions,
  ContextMenuActionDependencies,
} from '@/components/imageInputContextMenuUtils';
import { getProjectSelectedLayersId } from '#lib/project-runtime-layer-ui-state';
import { normalizeCollaborationMode } from '#lib/leasing/chat-sessions';
import {
  lastProjectIdOfSession,
  selectActiveSessionProjectIdFromInventoryState,
  resolveProjectIdFromChatSessionContext,
} from '#lib/leasing/chat-session-project-focus';
import { uploadFileToCloud } from '#lib/azure-client';
import { getFileExtension } from '#lib/utils';
import { useTablesStore } from '@/components/tables/tablesStore';
import {
  messageContentToText,
  normalizeSnippetText,
  parseTimelineMessagePayload,
} from '#lib/leasing/timeline-events';
import {
  areSubjectFocusesEquivalent,
  buildSubjectFocusHistoryKey,
  isEntityFocusSubject,
  isTableFocusSubject,
  deriveCreationScopeFromSubject,
  normalizeSubjectFocus,
  toFocusRuntimeMap,
  toSubjectFocusHistoryEntry,
} from '#lib/leasing/session-focus';
import { agentIsWorking } from '#lib/chat/agentIsWorking';
import { withoutChatSessionActions } from '#lib/chat/chatSessionDeletion';
import { isScriptExecuting } from '@/kernel/useCommands';
import { buildChatMessageMenuActions, downloadChatImage } from '#lib/chat/chatMessageMenu';
import { ATTACHMENTS_EVENT_TYPE } from '#lib/chat/attachments';
import { CHAT_ATTACHMENT_UPLOAD_FAILED } from '#lib/chat/attachmentKinds';

interface UseChatProps {
  kernel: RefObject<KernelHandle | null>;
}

const MENU_ICON_PROPS = { size: 18, className: 'text-white/90' } as const;

const CHAT_SESSION_DTO_AUTOSAVE_SCOPE = 'chat_session_dto_autosave';
const CHAT_SESSION_DTO_AUTOSAVE_DEBOUNCE_MS = 1200;
const DETACHED_MESSAGE_TTL_MONTHS = 2;
const MAX_SUBJECT_HISTORY_SIZE = 300;

const computeDetachedMessageExpiresAtIso = (): string => {
  const expiresAt = new Date();
  expiresAt.setUTCMonth(expiresAt.getUTCMonth() + DETACHED_MESSAGE_TTL_MONTHS);
  return expiresAt.toISOString();
};

const normalizeInitialSubject = (
  subject: ActiveEntityFocus | null | undefined
): ActiveEntityFocus | null => {
  return normalizeSubjectFocus(subject);
};

const getSubjectKey = (
  subject: { entityType?: EntityType; entityId?: string } | null | undefined
): string | null => {
  return buildSubjectFocusHistoryKey(normalizeSubjectFocus(subject));
};

const toActiveSubject = (
  subject: { entityType?: EntityType; entityId?: string } | null | undefined
): ActiveEntityFocus | null => {
  return normalizeSubjectFocus(subject);
};

const normalizeRuntimeSourcingSelection = (value: unknown): { portfolioId: string; propertyId: string; unitId: string } => {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
  const normalizeId = (raw: unknown) => (typeof raw === 'string' ? raw.trim() : '');
  return {
    portfolioId: normalizeId(source.portfolioId),
    propertyId: normalizeId(source.propertyId),
    unitId: normalizeId(source.unitId),
  };
};

const withRecoveredSourcingFocusAction = (
  kernelHandle: KernelHandle,
  subject: ActiveEntityFocus
): ActiveEntityFocus => {
  if (!isEntityFocusSubject(subject) || subject.focusActionType) return subject;
  if (subject.entityType !== 'Portfolio' && subject.entityType !== 'Property' && subject.entityType !== 'Unit') {
    return subject;
  }

  const selection = (() => {
    if (subject.entityType === 'Portfolio') {
      return normalizeRuntimeSourcingSelection(
        kernelHandle.readEntityRuntimeState('Portfolio', subject.entityId).sourcingSelection
      );
    }
    if (subject.entityType === 'Property') {
      return normalizeRuntimeSourcingSelection(
        kernelHandle.readEntityRuntimeState('Property', subject.entityId).sourcingSelection
      );
    }
    return normalizeRuntimeSourcingSelection(
      kernelHandle.readEntityRuntimeState('Unit', subject.entityId).sourcingSelection
    );
  })();

  const matchesSourcingSelection =
    (subject.entityType === 'Unit' && subject.entityId === selection.unitId) ||
    (subject.entityType === 'Property' && subject.entityId === selection.propertyId) ||
    (subject.entityType === 'Portfolio' && subject.entityId === selection.portfolioId);

  return matchesSourcingSelection
    ? { ...subject, focusActionType: 'ORCHESTRATE_SOURCING' }
    : subject;
};

const isEntityStillAvailable = (
  inventory: ReturnType<KernelHandle['getInventory']>,
  subject: ActiveEntityFocus | null | undefined
): boolean => {
  if (!subject) return false;
  if (isTableFocusSubject(subject)) return true;
  const registry = (inventory as any)?.[subject.entityType]?.registry as Record<string, unknown> | undefined;
  if (!registry) return false;
  return Boolean(registry[subject.entityId]);
};

const resolveActiveHistoryIndex = (
  historyEntries: ChatSubjectFocusEntry[],
  key: string,
  hint?: number | null
): number => {
  const hintedIndexRaw = hint;
  const hintedIndex =
    typeof hintedIndexRaw === 'number' && Number.isFinite(hintedIndexRaw)
      ? Math.floor(hintedIndexRaw)
      : -1;
  if (
    hintedIndex >= 0 &&
    hintedIndex < historyEntries.length &&
    getSubjectKey(historyEntries[hintedIndex]) === key
  ) {
    return hintedIndex;
  }
  for (let i = historyEntries.length - 1; i >= 0; i--) {
    if (getSubjectKey(historyEntries[i]) === key) {
      return i;
    }
  }
  return -1;
};

const findPreviousValidSubject = (
  session: ChatSession | null | undefined,
  inventory: ReturnType<KernelHandle['getInventory']>,
  options?: { activeHistoryIndexHint?: number | null; historyOverride?: ChatSubjectFocusEntry[] | null }
): { subject: ActiveEntityFocus | null; historyIndex: number; activeIndex: number } => {
  const active = session?.activeSubject ?? null;
  const currentKey = getSubjectKey(active);
  if (!currentKey) return { subject: null, historyIndex: -1, activeIndex: -1 };

  const historyOverride = Array.isArray(options?.historyOverride) ? options.historyOverride : null;
  const history: ChatSubjectFocusEntry[] = Array.isArray(historyOverride)
    ? historyOverride
    : Array.isArray(session?.subjectHistory)
      ? session!.subjectHistory
      : [];
  if (history.length === 0) return { subject: null, historyIndex: -1, activeIndex: -1 };

  const activeHistoryIndex = resolveActiveHistoryIndex(
    history,
    currentKey,
    options?.activeHistoryIndexHint
  );

  if (activeHistoryIndex === 0) {
    return { subject: null, historyIndex: -1, activeIndex: 0 };
  }

  const startIndex = activeHistoryIndex > 0 ? activeHistoryIndex - 1 : history.length - 1;
  for (let i = startIndex; i >= 0; i--) {
    const candidate = toActiveSubject(history[i]);
    if (!candidate) continue;
    if (getSubjectKey(candidate) === currentKey) continue;
    if (!isEntityStillAvailable(inventory, candidate)) continue;
    return { subject: candidate, historyIndex: i, activeIndex: activeHistoryIndex };
  }

  const firstSubject = toActiveSubject(history[0]);
  if (
    firstSubject &&
    getSubjectKey(firstSubject) !== currentKey &&
    isEntityStillAvailable(inventory, firstSubject)
  ) {
    return { subject: firstSubject, historyIndex: 0, activeIndex: activeHistoryIndex };
  }

  return { subject: null, historyIndex: -1, activeIndex: activeHistoryIndex };
};

const findNextValidSubject = (
  session: ChatSession | null | undefined,
  inventory: ReturnType<KernelHandle['getInventory']>,
  options?: { activeHistoryIndexHint?: number | null; historyOverride?: ChatSubjectFocusEntry[] | null }
): { subject: ActiveEntityFocus | null; historyIndex: number; activeIndex: number } => {
  const active = session?.activeSubject ?? null;
  const currentKey = getSubjectKey(active);
  if (!currentKey) return { subject: null, historyIndex: -1, activeIndex: -1 };

  const historyOverride = Array.isArray(options?.historyOverride) ? options.historyOverride : null;
  const history: ChatSubjectFocusEntry[] = Array.isArray(historyOverride)
    ? historyOverride
    : Array.isArray(session?.subjectHistory)
      ? session!.subjectHistory
      : [];
  if (history.length === 0) return { subject: null, historyIndex: -1, activeIndex: -1 };

  const activeHistoryIndex = resolveActiveHistoryIndex(
    history,
    currentKey,
    options?.activeHistoryIndexHint
  );

  if (activeHistoryIndex === history.length - 1) {
    return { subject: null, historyIndex: -1, activeIndex: activeHistoryIndex };
  }

  const startIndex = activeHistoryIndex >= 0 ? activeHistoryIndex + 1 : 0;
  for (let i = startIndex; i < history.length; i += 1) {
    const candidate = toActiveSubject(history[i]);
    if (!candidate) continue;
    if (getSubjectKey(candidate) === currentKey) continue;
    if (!isEntityStillAvailable(inventory, candidate)) continue;
    return { subject: candidate, historyIndex: i, activeIndex: activeHistoryIndex };
  }

  const lastIndex = history.length - 1;
  const lastSubject = lastIndex >= 0 ? toActiveSubject(history[lastIndex]) : null;
  if (
    lastSubject &&
    getSubjectKey(lastSubject) !== currentKey &&
    isEntityStillAvailable(inventory, lastSubject)
  ) {
    return { subject: lastSubject, historyIndex: lastIndex, activeIndex: activeHistoryIndex };
  }

  return { subject: null, historyIndex: -1, activeIndex: activeHistoryIndex };
};

const deriveLastMessageSnippet = (
  messages: Message[],
  fallback?: unknown
): string | null => {
  const fallbackSnippet = normalizeSnippetText(fallback);
  if (fallbackSnippet) return fallbackSnippet;
  const lastMessage = messages[messages.length - 1];
  if (!lastMessage) return null;
  return normalizeSnippetText(messageContentToText((lastMessage as unknown as Record<string, unknown>).content));
};

const buildSessionMessageMetadata = (messagesInput: Message[], fallbackSnippet?: unknown): {
  messageCount: number;
  lastMessageSnippet: string | null;
} => {
  const messages = Array.isArray(messagesInput) ? messagesInput : [];
  return {
    messageCount: messages.length,
    lastMessageSnippet: deriveLastMessageSnippet(messages, fallbackSnippet),
  };
};

const getMessageTimelineEventId = (message: Message): string => {
  const timelineEventId =
    typeof message.timelineEventId === 'string' ? message.timelineEventId.trim() : '';
  return timelineEventId;
};

const getMessageFallbackKey = (message: Message): string =>
  `${message.role}:${String(message.timestamp ?? '')}`;

const toComparableMessage = (message: Message): Record<string, unknown> => {
  const { timelineEventId: _ignoredTimelineEventId, ...rest } = message;
  return rest as unknown as Record<string, unknown>;
};

const mergeMessageWithOverlay = (base: Message, overlay: Message): Message => {
  const baseTimelineEventId = getMessageTimelineEventId(base);
  const overlayTimelineEventId = getMessageTimelineEventId(overlay);
  const merged: Message = {
    ...base,
    ...overlay,
  };

  if (baseTimelineEventId) {
    merged.timelineEventId = baseTimelineEventId;
  } else if (overlayTimelineEventId) {
    merged.timelineEventId = overlayTimelineEventId;
  }

  return merged;
};

// Sessions currently being auto-titled (client singleton) so a burst of turn
// completions doesn't fire duplicate titling requests for the same session.
const AUTO_TITLING_IN_FLIGHT = new Set<string>();
const isDefaultSessionName = (name: string | null | undefined): boolean => {
  const n = (name || '').trim();
  return !n || n === 'New chat' || n === 'New Chat' || n === 'Global Session' || n === 'New conversation';
};

const toTimelineMessageEventType = (
  role: Message['role']
): TimelineEventType => {
  if (role === 'assistant') return 'ASSISTANT_MESSAGE';
  if (role === 'system') return 'SYSTEM_MESSAGE';
  return 'USER_MESSAGE';
};

const MAX_TIMELINE_SYSTEM_MESSAGE_LENGTH = 220;
const MAX_TIMELINE_EVENT_FIELD_COUNT = 4;

const normalizeNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const clampTimelineMessage = (value: string): string => {
  if (value.length <= MAX_TIMELINE_SYSTEM_MESSAGE_LENGTH) return value;
  return `${value.slice(0, MAX_TIMELINE_SYSTEM_MESSAGE_LENGTH - 3)}...`;
};

const resolveTimelineEventEntityLabel = (
  event: TimelineEventDTO,
  payload: Record<string, unknown>
): string | null => {
  const payloadEntityType = normalizeNonEmptyString(payload.entityType);
  const payloadEntityId = normalizeNonEmptyString(payload.entityId);
  const eventEntityType = normalizeNonEmptyString(event.targetEntityType);
  const eventEntityId = normalizeNonEmptyString(event.targetEntityId);

  const entityType = payloadEntityType ?? eventEntityType;
  const entityId = payloadEntityId ?? eventEntityId;
  if (!entityType || !entityId) return null;
  return `${entityType}:${entityId}`;
};

const summarizeTimelinePayloadFields = (payload: Record<string, unknown>): string | null => {
  const blockedKeys = new Set(['content', 'prompt', 'negativePrompt', 'images', 'imageUrls', 'image']);
  const keys = Object.keys(payload).filter((key) => !blockedKeys.has(key));
  if (keys.length === 0) return null;
  return keys.slice(0, MAX_TIMELINE_EVENT_FIELD_COUNT).join(', ');
};

const projectTimelineEventToMessage = (event: TimelineEventDTO): Message | null => {
  if (event.category === 'TELEMETRY') return null;

  if (event.category === 'COMMUNICATION') {
    const parsed = parseTimelineMessagePayload(event.payload);
    if (!parsed) return null;
    return {
      ...parsed,
      timelineEventId: event.id,
    } as Message;
  }

  const payload = isRecord(event.payload) ? event.payload : {};
  const eventType = normalizeNonEmptyString(event.eventType) ?? 'UNKNOWN_EVENT';
  const eventTypeUpper = eventType.toUpperCase();
  const entityLabel = resolveTimelineEventEntityLabel(event, payload);

  let content: string;
  if (eventTypeUpper === 'STUDIO_GENERATION_TASK_SPAWNED') {
    const details: string[] = [];
    const taskType = normalizeNonEmptyString(payload.taskType);
    const model = normalizeNonEmptyString(payload.model);
    if (taskType) details.push(`type=${taskType}`);
    if (model) details.push(`model=${model}`);
    if (entityLabel) details.push(`target=${entityLabel}`);
    content = `Generation task spawned${details.length > 0 ? ` (${details.join(', ')})` : ''}`;
  } else if (
    eventTypeUpper === 'ENTITY_ANCHORED' ||
    eventTypeUpper === 'ENTITY_UNANCHORED' ||
    eventTypeUpper === 'SUBJECT_FOCUSED' ||
    eventTypeUpper === 'SUBJECT_CLEARED' ||
    eventTypeUpper === ATTACHMENTS_EVENT_TYPE
  ) {
    // Bookkeeping, not speech. components/portal/useSessionMessages.ts hides these, it is the
    // projector ChatTab actually renders, and this one was left printing
    // "Context anchored (Property:cmld…)" with a raw id. Nothing surfaces it today, so the fix
    // was one-sided; a second projector that disagrees is how the string comes back.
    return null;
  } else {
    const fieldSummary = summarizeTimelinePayloadFields(payload);
    const categoryLabel = event.category === 'MUTATION' ? 'Mutation' : 'Action';
    content = `${categoryLabel}: ${eventType}`;
    if (fieldSummary) {
      content = `${content} (${fieldSummary})`;
    }
  }

  const timestamp = normalizeNonEmptyString(event.createdAt) ?? new Date().toISOString();
  return {
    role: 'system',
    content: clampTimelineMessage(content),
    timestamp,
    timelineEventId: event.id,
    ...(normalizeNonEmptyString(event.targetEntityType) && normalizeNonEmptyString(event.targetEntityId)
      ? {
          targetEntityType: normalizeNonEmptyString(event.targetEntityType) as string,
          targetEntityId: normalizeNonEmptyString(event.targetEntityId) as string,
        }
      : {}),
  };
};

const LOCAL_ENTITY_DELETE_MARKER = '_isDeletedLocally';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (!isRecord(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

type PersistedChatSessionPayload = {
  id: string;
  name: string;
  ownerId: string | null;
  strategicPlan: string | null;
  messageCount?: number;
  lastMessageSnippet?: string | null;
  contextSummary?: ChatSession['contextSummary'];
  activeSubject?: ActiveEntityFocus | null;
  subjectHistory?: ChatSession['subjectHistory'];
  workingMemory?: ChatSession['workingMemory'];
  createdAt: string;
  updatedAt: string;
  collaborationMode?: CollaborationMode;
};

const toPersistedChatSessionPayload = (
  session: ChatSessionInventoryEntityState
): PersistedChatSessionPayload => {
  const normalizedWorkingMemory =
    session.workingMemory && Array.isArray(session.workingMemory.anchors)
      ? {
          ...session.workingMemory,
          anchors: session.workingMemory.anchors
            .filter(
              (anchor) =>
                typeof anchor?.entityType === 'string' &&
                typeof anchor?.entityId === 'string' &&
                anchor.entityId.trim().length > 0
            )
            .map((anchor) => ({
              ...anchor,
              entityId: anchor.entityId.trim(),
              mass:
                typeof anchor.mass === 'number' && Number.isFinite(anchor.mass)
                  ? Math.max(1, Math.floor(anchor.mass))
                  : 1,
              lastAccessedAt:
                typeof anchor.lastAccessedAt === 'number' && Number.isFinite(anchor.lastAccessedAt)
                  ? Math.floor(anchor.lastAccessedAt)
                  : Date.now(),
            })),
        }
      : undefined;

  const payload: PersistedChatSessionPayload = {
    id: session.id,
    name: session.name,
    ownerId: session.ownerId ?? null,
    strategicPlan: session.strategicPlan ?? null,
    messageCount:
      typeof session.messageCount === 'number' && Number.isFinite(session.messageCount)
        ? Math.max(0, Math.floor(session.messageCount))
        : 0,
    lastMessageSnippet:
      typeof session.lastMessageSnippet === 'string' ? session.lastMessageSnippet : null,
    contextSummary: Array.isArray(session.contextSummary) ? session.contextSummary : undefined,
    activeSubject: session.activeSubject ?? null,
    subjectHistory: Array.isArray(session.subjectHistory) ? session.subjectHistory : undefined,
    workingMemory: normalizedWorkingMemory,
    createdAt: session.createdAt,
    updatedAt: session.updatedAt,
  };

  const collaborationMode = normalizeCollaborationMode(session.collaborationMode);
  if (collaborationMode) {
    payload.collaborationMode = collaborationMode;
  }

  return payload;
};

const deepEqual = (left: unknown, right: unknown): boolean => {
  if (Object.is(left, right)) return true;
  if (Array.isArray(left) && Array.isArray(right)) {
    if (left.length !== right.length) return false;
    for (let i = 0; i < left.length; i += 1) {
      if (!deepEqual(left[i], right[i])) return false;
    }
    return true;
  }
  if (isPlainObject(left) && isPlainObject(right)) {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;
    for (const key of leftKeys) {
      if (!Object.prototype.hasOwnProperty.call(right, key)) return false;
      if (!deepEqual(left[key], right[key])) return false;
    }
    return true;
  }
  return false;
};

export const useChat = ({ kernel }: UseChatProps): useChatReturnType => {
  const upsertChatSessionDraft = useCallback((session: ChatSession) => {
    const k = kernel.current;
    if (!k) return;

    const sessionId = session.id;
    const rawSession = (k.getState().inventory.ChatSession.registry as Record<string, unknown>)[sessionId];
    const currentDraft = k.readEntityKernelState('ChatSession', sessionId);
    if (isRecord(currentDraft) && currentDraft[LOCAL_ENTITY_DELETE_MARKER] === true) {
      k.removeEntityKernelState('ChatSession', sessionId);
    }

    if (deepEqual(rawSession, session)) {
      k.removeEntityKernelState('ChatSession', sessionId);
      return;
    }

    k.mergeEntityKernelState('ChatSession', sessionId, session as unknown as Record<string, unknown>);
  }, [kernel]);

  const readPendingSessionMessages = useCallback((sessionId: string): Message[] => {
    const k = kernel.current;
    if (!k) return [];
    const runtime = k.readEntityRuntimeState('ChatSession', sessionId);
    return Array.isArray(runtime.pendingMessages) ? runtime.pendingMessages : [];
  }, [kernel]);

  const writePendingSessionMessages = useCallback((sessionId: string, messages: Message[]): void => {
    const k = kernel.current;
    if (!k) return;
    k.patchEntityRuntimeState('ChatSession', sessionId, {
      pendingMessages: Array.isArray(messages) ? messages : [],
    });
  }, [kernel]);

  const isCommunicationTimelineEventId = useCallback((eventId: string): boolean => {
    const normalizedEventId = typeof eventId === 'string' ? eventId.trim() : '';
    if (!normalizedEventId) return false;
    const k = kernel.current;
    if (!k) return false;
    const timelineEvent = k.getInventory().TimelineEvent.registry[normalizedEventId];
    return timelineEvent?.category === 'COMMUNICATION';
  }, [kernel]);

  const listTimelineMessagesForSession = useCallback((sessionId: string): Message[] => {
    const k = kernel.current;
    if (!k) return [];

    const timelineRegistry = k.getInventory().TimelineEvent.registry;
    const indexedTimelineIds = k.getState().timelineFeedIndex.bySessionId[sessionId] ?? [];
    const timelineEvents = indexedTimelineIds
      .map((eventId) => timelineRegistry[eventId] ?? null)
      .filter((event): event is TimelineEventDTO => {
        if (!event) return false;
        return event.category !== 'TELEMETRY';
      })
      .sort((left, right) => {
        const leftTs = new Date(left.createdAt).getTime();
        const rightTs = new Date(right.createdAt).getTime();
        if (leftTs === rightTs) return left.id.localeCompare(right.id);
        return leftTs - rightTs;
      });

    return timelineEvents
      .map((event) => projectTimelineEventToMessage(event))
      .filter((message): message is Message => Boolean(message));
  }, [kernel]);

  const mergeTimelineAndPendingMessages = useCallback((timelineMessages: Message[], pendingMessages: Message[]): Message[] => {
    const mergedMessages = [...timelineMessages];
    const indexByTimelineEventId = new Map<string, number>();
    const indexByFallbackKey = new Map<string, number>();

    mergedMessages.forEach((message, index) => {
      const timelineEventId = getMessageTimelineEventId(message);
      if (timelineEventId) {
        indexByTimelineEventId.set(timelineEventId, index);
      }
      indexByFallbackKey.set(getMessageFallbackKey(message), index);
    });

    pendingMessages.forEach((message) => {
      const timelineEventId = getMessageTimelineEventId(message);
      const fallbackKey = getMessageFallbackKey(message);

      const existingIndexByTimelineEventId =
        timelineEventId && indexByTimelineEventId.has(timelineEventId)
          ? indexByTimelineEventId.get(timelineEventId)
          : undefined;
      const existingIndexByFallback =
        indexByFallbackKey.has(fallbackKey)
          ? indexByFallbackKey.get(fallbackKey)
          : undefined;
      const existingIndex =
        typeof existingIndexByTimelineEventId === 'number'
          ? existingIndexByTimelineEventId
          : existingIndexByFallback;

      if (typeof existingIndex === 'number' && mergedMessages[existingIndex]) {
        const merged = mergeMessageWithOverlay(mergedMessages[existingIndex]!, message);
        mergedMessages[existingIndex] = merged;
        const mergedTimelineEventId = getMessageTimelineEventId(merged);
        if (mergedTimelineEventId) {
          indexByTimelineEventId.set(mergedTimelineEventId, existingIndex);
        }
        indexByFallbackKey.set(getMessageFallbackKey(merged), existingIndex);
        return;
      }

      const nextIndex = mergedMessages.length;
      mergedMessages.push(message);
      if (timelineEventId) {
        indexByTimelineEventId.set(timelineEventId, nextIndex);
      }
      indexByFallbackKey.set(fallbackKey, nextIndex);
    });

    mergedMessages.sort(
      (left, right) =>
        new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime()
    );
    return mergedMessages;
  }, []);

  const applySessionMessageProjection = useCallback((sessionId: string, messages: Message[], options?: { touchUpdatedAt?: boolean }) => {
    const k = kernel.current;
    if (!k) return;
    const session = k.getInventory().ChatSession.registry[sessionId];
    if (!session) return;

    const updatedSession: ChatSession = {
      ...session,
      ...buildSessionMessageMetadata(
        messages,
        session.lastMessageSnippet ?? null
      ),
      ...(options?.touchUpdatedAt === true ? { updatedAt: new Date().toISOString() } : {}),
    };
    upsertChatSessionDraft(updatedSession);
  }, [kernel]);

  const getChatSessionMessages = useCallback((sessionId?: string | null): Message[] => {
    const k = kernel.current;
    if (!k) return [];
    const targetSessionId = typeof sessionId === 'string' ? sessionId.trim() : k.getActiveChatSessionId();
    if (!targetSessionId) return [];
    const session = k.getInventory().ChatSession.registry[targetSessionId];
    if (!session) return [];

    const timelineMessages = listTimelineMessagesForSession(targetSessionId);
    const pendingMessages = readPendingSessionMessages(targetSessionId);
    return mergeTimelineAndPendingMessages(timelineMessages, pendingMessages);
  }, [kernel, listTimelineMessagesForSession, mergeTimelineAndPendingMessages, readPendingSessionMessages]);

  const syncSessionMessagesFromTimelineRegistry = useCallback((sessionId: string) => {
    const k = kernel.current;
    const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (!k || !normalizedSessionId) return;

    const session = k.getInventory().ChatSession.registry[normalizedSessionId];
    if (!session) return;

    const timelineMessages = listTimelineMessagesForSession(normalizedSessionId);
    const pendingLocalMessages = readPendingSessionMessages(normalizedSessionId);
    const timelineByEventId = new Map<string, Message>();
    const timelineByFallbackKey = new Map<string, Message>();
    timelineMessages.forEach((message) => {
      const timelineEventId = getMessageTimelineEventId(message);
      if (timelineEventId) {
        timelineByEventId.set(timelineEventId, message);
      }
      timelineByFallbackKey.set(getMessageFallbackKey(message), message);
    });

    const filteredPending = pendingLocalMessages.filter((message) => {
      const timelineEventId = getMessageTimelineEventId(message);
      const fallbackKey = getMessageFallbackKey(message);

      const timelineMatch =
        (timelineEventId ? timelineByEventId.get(timelineEventId) : undefined) ??
        timelineByFallbackKey.get(fallbackKey);
      if (!timelineMatch) return true;

      const comparableTimeline = toComparableMessage(timelineMatch);
      const comparableMergedPending = toComparableMessage(
        mergeMessageWithOverlay(timelineMatch, message)
      );
      if (deepEqual(comparableTimeline, comparableMergedPending)) {
        return false;
      }

      return true;
    });

    if (!deepEqual(filteredPending, pendingLocalMessages)) {
      writePendingSessionMessages(normalizedSessionId, filteredPending);
    }

    const mergedMessages = mergeTimelineAndPendingMessages(timelineMessages, filteredPending);
    applySessionMessageProjection(normalizedSessionId, mergedMessages);
  }, [
    applySessionMessageProjection,
    kernel,
    listTimelineMessagesForSession,
    mergeTimelineAndPendingMessages,
    readPendingSessionMessages,
    writePendingSessionMessages,
  ]);

  const removeChatSessionDraft = useCallback((sessionId: string) => {
    const k = kernel.current;
    if (!k || !sessionId) return;

    const hasRawSession = Boolean(k.getState().inventory.ChatSession.registry[sessionId]);
    k.removeEntityKernelState('ChatSession', sessionId);
    if (hasRawSession) {
      k.mergeEntityKernelState('ChatSession', sessionId, {
        [LOCAL_ENTITY_DELETE_MARKER]: true,
      });
    }
  }, [kernel]);

  const cancelQueuedSessionSave = useCallback((sessionId: string) => {
    const k = kernel.current;
    if (!k) return;
    k.cancelAutosave('ChatSession', sessionId, CHAT_SESSION_DTO_AUTOSAVE_SCOPE);
  }, [kernel]);

  const setActiveChatSessionIdInternal: Setter<string | null> = useCallback((updater) => {
    if (!kernel.current) return;
    const currentId = kernel.current.getActiveChatSessionId();
    const newActiveId = typeof updater === 'function' ? (updater as any)(currentId) : updater;
    kernel.current.setState({ activeChatSessionId: newActiveId });
  }, [kernel]);

  const clearActiveChatSession = useCallback(() => {
    kernel.current?.setState({ sourceOpeningRentRollId: null });
    setActiveChatSessionIdInternal(null);
  }, [kernel, setActiveChatSessionIdInternal]);

  const startThinkingSequence = useCallback((sessionId: string) => {
    const k = kernel.current;
    const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (!k || !normalizedSessionId) return;

    // SETTLED LINES SURVIVE THIS. Starting to think replaces what is MOVING, not the record: a run
    // that finished leaves "Scored 5 of 5 brands" in the transcript at the moment it ended, and
    // wiping the strip wholesale would delete it the instant the agent was asked anything else.
    // That matters more now than it used to, because a continuation turn calls this in the middle
    // of an answer rather than only at the start of one.
    const previous = k.readEntityRuntimeState('ChatSession', normalizedSessionId).chatState ?? [];
    const settled = previous.filter((entry) => entry?.settled === true);
    k.patchEntityRuntimeState('ChatSession', normalizedSessionId, {
      chatState: [...settled, { text: 'Thinking', startTime: Date.now() }],
    });
  }, [kernel]);

  // Live activity is one replaceable slot; settled outcomes remain in the transcript.
  const setSessionStatus = useCallback((
    sessionId: string,
    status: string,
    options?: { replacesPrevious?: boolean; settled?: boolean; ambient?: boolean; brands?: ChatStatusBrand[] },
  ) => {
    if (!kernel.current) return;
    const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (!normalizedSessionId) return;

    if (status === '') {
      kernel.current.patchEntityRuntimeState('ChatSession', normalizedSessionId, { chatState: [] });
      return;
    }

    const currentHistory = kernel.current.readEntityRuntimeState('ChatSession', normalizedSessionId).chatState ?? [];
    const settled = currentHistory.filter((item) => item.settled === true);
    const lastSettledIndex = currentHistory.map((item) => item.settled === true).lastIndexOf(true);
    const previous = currentHistory[lastSettledIndex + 1];
    const entry = {
      text: status,
      settled: options?.settled === true,
      ...(options?.ambient ? { ambient: true } : {}),
      ...(options?.brands?.length ? { brands: options.brands } : {}),
    };
    const next = [...settled, { ...entry, startTime: entry.settled ? Date.now() : previous?.startTime ?? Date.now() }];

    kernel.current.patchEntityRuntimeState('ChatSession', normalizedSessionId, { chatState: next });
  }, [kernel]);

  const getLastProjectId = useCallback((sessionId: string): string | null => {
    const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (!normalizedSessionId) return null;
    return lastProjectIdOfSession(kernel.current?.getInventory().ChatSession.registry[normalizedSessionId]);
  }, [kernel]);

  const resolveActiveSessionProjectId = useCallback((): string => {
    const k = kernel.current;
    if (!k) return '';

    const inventory = k.getInventory();
    return (
      selectActiveSessionProjectIdFromInventoryState({
        activeChatSessionId: k.getActiveChatSessionId(),
        inventory: {
          ChatSession: {
            registry: inventory.ChatSession.registry,
          },
        },
      }) ?? ''
    );
  }, [kernel]);

  const resolveSessionDefaultCollaborationMode = useCallback((): CollaborationMode => {
    const k = kernel.current;
    if (!k) return 'ASSISTED';
    // User preference wins, then the org default, then ASSISTED, the safe default:
    // require per-step confirmation unless the user or org has explicitly opted into autonomy.
    const userDefault = normalizeCollaborationMode(k.getUserPreferences()?.defaultCollaborationMode);
    if (userDefault) return userDefault;
    const userOrganisationId = k.getUserContext().organisationId;
    if (userOrganisationId) {
      const organisation = k.getInventory().Organisation.registry[userOrganisationId];
      const orgDefault = normalizeCollaborationMode(organisation?.defaultCollaborationMode);
      if (orgDefault) return orgDefault;
    }
    return 'ASSISTED';
  }, [kernel]);

  const requestScriptStop = useCallback((sessionId?: string) => {
    if (!kernel.current) return;

    const targetSessionId = sessionId || kernel.current.getActiveChatSessionId();
    if (!targetSessionId) return;
    const normalizedSessionId = typeof targetSessionId === 'string' ? targetSessionId.trim() : '';
    if (!normalizedSessionId) return;

    const controller = kernel.current.readEntityRuntimeState('ChatSession', normalizedSessionId).abortController;
    
    if (controller && typeof (controller as { abort?: unknown }).abort === 'function') {
      controller.abort();
      console.log(`[Kernel] Aborted controller for session ${targetSessionId}`);
    } else if (controller) {
      console.warn(
        `[Kernel] Session ${targetSessionId} has a non-abortable runtime controller.`,
        controller
      );
    }

    kernel.current.patchEntityRuntimeState('ChatSession', normalizedSessionId, { isStopRequested: true, slidesButtonIntent: null, listGenerationAgreementScriptId: null });
    const slides = useSlidesStore.getState();
    void slides.finishAgentRun(slides.getAgentRun(normalizedSessionId), 'stopped');
    kernel.current.cancelGenerationTasksForSession(normalizedSessionId);
  }, [kernel]);

  const createAndActivateChatSession = useCallback(async (
    initialPrompt: string,
    options?: {
      initialSubject?: ActiveEntityFocus | null;
      creationScope?: SessionCreationScope | null;
    }
  ) => {
    if (!kernel.current) return;

    const activeSessionId = kernel.current.getActiveChatSessionId();
    const projectId = activeSessionId ? getLastProjectId(activeSessionId) : null;
    const tempId = uuidv4();
    const createdAt = new Date().toISOString();
    const defaultSessionCollaborationMode = resolveSessionDefaultCollaborationMode();
    const initialSubject =
      normalizeInitialSubject(options?.initialSubject) ??
      (projectId
        ? ({
            entityType: 'Project',
            entityId: projectId,
          } as ActiveEntityFocus)
        : null);
    const creationScope =
      options?.creationScope ?? deriveCreationScopeFromSubject(initialSubject);

    const newSession: ChatSession = {
      id: tempId,
      name: 'New Chat',
      ownerId: kernel.current.getUserContext().dbUserId,
      messageCount: 0,
      strategicPlan: null,
      collaborationMode: defaultSessionCollaborationMode,
      createdAt,
      updatedAt: createdAt,
      isDirty: false,
      saveStatus: 'saving',
      activeSubject: initialSubject,
      subjectHistory: initialSubject
        ? [toSubjectFocusHistoryEntry(initialSubject, createdAt)]
        : [],
      creationModule: creationScope.module,
      creationEntityType: creationScope.entityType ?? null,
      creationEntityId: creationScope.entityId ?? null,
    };

    upsertChatSessionDraft(newSession);
    setActiveChatSessionIdInternal(tempId);

    try {
      const sessionPayload = toPersistedChatSessionPayload(
        newSession as ChatSessionInventoryEntityState
      );
      const createPayload: Record<string, unknown> = { ...sessionPayload };
      delete createPayload.id;
      // Scope rides the CREATE payload only, the autosave payload
      // (toPersistedChatSessionPayload) deliberately never carries it, and the
      // update mutation schema rejects it (immutable home).
      createPayload.creationModule = creationScope.module;
      createPayload.creationEntityType = creationScope.entityType ?? null;
      createPayload.creationEntityId = creationScope.entityId ?? null;
      const result = await kernel.current.mutateInventory([{
        action: 'CREATE',
        entity: 'ChatSession',
        tempId: `chat-session-create-${tempId}`,
        payload: createPayload,
      }], 'Create chat session from composer prompt');
      const createdSessionIdRaw = result[0]?.id;
      const createdSessionId =
        typeof createdSessionIdRaw === 'string' ? createdSessionIdRaw.trim() : '';
      if (!createdSessionId) {
        throw new Error('ChatSession create mutation did not return an id.');
      }

      await kernel.current.syncEntity('ChatSession', createdSessionId).catch(() => undefined);
      const canonicalCreatedSession =
        kernel.current.getInventory().ChatSession.registry[createdSessionId] ?? null;
      const realSession: ChatSession = canonicalCreatedSession
        ? { ...canonicalCreatedSession, isDirty: false, saveStatus: 'saved' }
        : {
            ...newSession,
            id: createdSessionId,
            isDirty: false,
            saveStatus: 'saved',
          };

      removeChatSessionDraft(tempId);
      upsertChatSessionDraft(realSession);
      setActiveChatSessionIdInternal(createdSessionId);
      if (initialSubject) {
        kernel.current.setActiveSubject(createdSessionId, initialSubject);
      }

      const createdProjectId = getLastProjectId(createdSessionId) ?? '';
      const activeSubject = initialSubject ?? realSession.activeSubject;
      const executionAgentMode =
        activeSubject?.entityType === 'Project' && createdProjectId
          ? 'director'
          : 'commander';
      await kernel.current.execute(initialPrompt, undefined, {
        agentMode: executionAgentMode,
        projectId: createdProjectId,
      });
    } catch (error) {
      console.error("Failed to create chat session", error);
      kernel.current.handleError("Failed to create chat session.");
    }
  }, [
    getLastProjectId,
    kernel,
    removeChatSessionDraft,
    resolveSessionDefaultCollaborationMode,
    setActiveChatSessionIdInternal,
    upsertChatSessionDraft,
  ]);

  const renameChatSession = useCallback((sessionId: string, newName: string) => {
    const kernelRef = kernel.current;
    if (!kernelRef) return;

    const existing = kernelRef.getInventory().ChatSession.registry[sessionId];
    if (!existing) return;

    const updatedAt = new Date().toISOString();

    const updated: ChatSession = {
      ...existing,
      name: newName,
      updatedAt,
      isDirty: true,
    };
    upsertChatSessionDraft(updated);

    kernelRef.markChatSessionDirty?.(sessionId);
    kernelRef.logUserAction('Chat Session Renamed', { session_id: sessionId });
  }, [kernel]);

  const deleteChatSession = useCallback((sessionId: string) => {
    if (!kernel.current) return;
    if (!kernel.current.getInventory().ChatSession.registry[sessionId]) return;

    // Final, outside the undo history: the server delete cascades the messages, and a
    // re-CREATE cannot restore the immutable creation scope.
    cancelQueuedSessionSave(sessionId);
    if (kernel.current.getActiveChatSessionId() === sessionId) {
      clearActiveChatSession();
    }
    removeChatSessionDraft(sessionId);
    kernel.current.setUndoStack((stack) => withoutChatSessionActions(stack, sessionId));
    kernel.current.setRedoStack((stack) => withoutChatSessionActions(stack, sessionId));
    void kernel.current.mutateInventory([{
      action: 'DELETE',
      entity: 'ChatSession',
      id: sessionId,
    }], `Delete ChatSession ${sessionId}`).catch((error) => {
      console.error('[useChat] Failed to delete session from inventory mutate', error);
    });
    kernel.current.logUserAction('Chat Session Deleted', { session_id: sessionId });
  }, [
    cancelQueuedSessionSave,
    clearActiveChatSession,
    kernel,
    removeChatSessionDraft,
  ]);

  const deleteChatSessionSilently = useCallback(async (sessionId: string) => {
    if (!kernel.current || !sessionId) return;
    const session = kernel.current.getInventory().ChatSession.registry[sessionId];
    if (!session) return;

    if (kernel.current.getActiveChatSessionId() === sessionId) {
      clearActiveChatSession();
    }

    cancelQueuedSessionSave(sessionId);
    removeChatSessionDraft(sessionId);
    
    try {
      await kernel.current.mutateInventory([{
        action: 'DELETE',
        entity: 'ChatSession',
        id: sessionId,
      }], `Silent delete ChatSession ${sessionId}`);
    } catch (error) {
      console.error('[useChat] Failed to silently delete session', error);
    }
  }, [cancelQueuedSessionSave, clearActiveChatSession, kernel, removeChatSessionDraft]);

  const createMessageTimelineEvent = useCallback(async ({
    sessionId,
    message,
    eventType,
    targetEntityType,
    targetEntityId,
    signal,
    requirePersistence,
  }: {
    sessionId: string;
    message: Message;
    eventType: TimelineEventType;
    targetEntityType?: string | null;
    targetEntityId?: string | null;
    signal?: AbortSignal;
    requirePersistence?: boolean;
  }): Promise<string | null> => {
    const k = kernel.current;
    if (!k) return null;

    try {
      const result = await k.mutateInventory([{
        action: 'CREATE',
        entity: 'TimelineEvent',
        tempId: `timeline-event-${uuidv4()}`,
        payload: {
          chatSessionId: sessionId,
          category: 'COMMUNICATION',
          eventType,
          payload: message as unknown as Record<string, unknown>,
          ...(targetEntityType ? { targetEntityType } : {}),
          ...(targetEntityId ? { targetEntityId } : {}),
        },
      }], `Create TimelineEvent ${eventType} for session ${sessionId}`, signal);
      const createdId = result[0]?.id;
      return typeof createdId === 'string' && createdId.trim().length > 0 ? createdId : null;
    } catch (error) {
      if (requirePersistence) throw error;
      console.error('[useChat] Failed to create timeline message event', error);
      return null;
    }
  }, [kernel]);

  const detachTimelineMessageEvent = useCallback(async (eventId: string): Promise<number> => {
    const k = kernel.current;
    const normalizedEventId = typeof eventId === 'string' ? eventId.trim() : '';
    if (!k || !normalizedEventId) return 0;

    const detachedExpiresAt = computeDetachedMessageExpiresAtIso();
    const rawTimelineEvent = k.getState().inventory.TimelineEvent.registry[normalizedEventId];
    if (rawTimelineEvent) {
      k.mergeEntityKernelState('TimelineEvent' as any, normalizedEventId, {
        chatSessionId: null,
        expiresAt: detachedExpiresAt,
      });
    }

    try {
      await k.mutateInventory([{
        action: 'UPDATE',
        entity: 'TimelineEvent',
        id: normalizedEventId,
        payload: {
          chatSessionId: null,
          expiresAt: detachedExpiresAt,
        },
      }], `Detach TimelineEvent ${normalizedEventId} from session`);
      return 1;
    } catch (error) {
      console.error('[useChat] Failed to detach timeline message event', { eventId: normalizedEventId, error });
      return 0;
    }
  }, [kernel]);

  const restoreDetachedTimelineMessageEvent = useCallback(async (
    sessionId: string,
    eventId: string,
  ): Promise<number> => {
    const k = kernel.current;
    const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
    const normalizedEventId = typeof eventId === 'string' ? eventId.trim() : '';
    if (!k || !normalizedSessionId || !normalizedEventId) return 0;

    try {
      await k.mutateInventory([{
        action: 'UPDATE',
        entity: 'TimelineEvent',
        id: normalizedEventId,
        payload: {
          chatSessionId: normalizedSessionId,
          expiresAt: null,
        },
      }], `Restore TimelineEvent ${normalizedEventId} into session ${normalizedSessionId}`);
      return 1;
    } catch (error) {
      console.error('[useChat] Failed to restore detached timeline message event', {
        sessionId: normalizedSessionId,
        eventId: normalizedEventId,
        error,
      });
      return 0;
    }
  }, [kernel]);

  const queueMessagePersistence = useCallback((sessionId: string, write: () => Promise<void>, recover = false) => {
    const k = kernel.current;
    if (!k) return Promise.resolve();
    const userId = k.getUserContext().dbUserId;
    const previous = k.readEntityRuntimeState('ChatSession', sessionId).messagePersistence;
    const predecessor = previous ?? Promise.resolve();
    const pending = (recover ? predecessor.catch(() => undefined) : predecessor).then(async () => {
      if (!kernel.current || kernel.current.getUserContext().dbUserId !== userId) throw new Error('The account changed before the message could be saved.');
      await write();
      if (!kernel.current || kernel.current.getUserContext().dbUserId !== userId) throw new Error('The account changed while the message was being saved.');
    });
    k.patchEntityRuntimeState('ChatSession', sessionId, { messagePersistence: pending });
    void pending.catch(() => undefined);
    return pending;
  }, [kernel]);

  const updateMessageTimelineEvent = useCallback(async (eventId: string, message: Message): Promise<void> => {
    const k = kernel.current;
    if (!k || !eventId) return;

    try {
      await k.mutateInventory([{
        action: 'UPDATE',
        entity: 'TimelineEvent',
        id: eventId,
        payload: {
          payload: message as unknown as Record<string, unknown>,
        },
      }], `Update TimelineEvent ${eventId}`, message.scriptsInstances?.some(requiresListScriptPersistence) ? AbortSignal.timeout(30_000) : undefined);
    } catch (error) {
      if (message.scriptsInstances?.some(requiresListScriptPersistence)) throw error;
      // Log the MESSAGE, not the Error object: an Error inside an object
      // literal serialises to {} in the browser console, which is exactly what
      // this line printed the last time it fired - "Failed to update timeline
      // message event {}" tells nobody anything.
      console.error(
        `[useChat] Failed to update timeline message event ${eventId}:`,
        error instanceof Error ? error.message : String(error)
      );
    }
  }, [kernel]);

  const updateActiveSessionMessages = useCallback((updater: (prevMessages: Message[]) => Message[], targetSessionId?: string | null) => {
    const k = kernel.current;
    if (!k) return;
    const activeId = targetSessionId ?? k.getActiveChatSessionId();
    if (!activeId) return;

    const session = k.getInventory().ChatSession.registry[activeId];
    if (!session) return;

    const previousMessages = getChatSessionMessages(activeId);
    const nextMessages = updater(previousMessages);
    const timelineRegistry = k.getInventory().TimelineEvent.registry;
    const pendingMessages = nextMessages.filter((message) => {
      const timelineEventId = getMessageTimelineEventId(message);
      if (!timelineEventId) return true;
      const timelineEvent = timelineRegistry[timelineEventId];
      if (!timelineEvent) return true;
      if (timelineEvent.category !== 'COMMUNICATION') return false;
      const timelineParsed = parseTimelineMessagePayload(timelineEvent.payload);
      if (!timelineParsed) return true;
      const timelineMessage: Message = {
        ...timelineParsed,
        timelineEventId: timelineEvent.id,
      };
      const comparableTimeline = toComparableMessage(timelineMessage);
      const comparableMergedPending = toComparableMessage(
        mergeMessageWithOverlay(timelineMessage, message)
      );
      return !deepEqual(comparableTimeline, comparableMergedPending);
    });

    writePendingSessionMessages(activeId, pendingMessages);
    applySessionMessageProjection(activeId, nextMessages, { touchUpdatedAt: true });

    const previousByTimelineEventId = new Map<string, Message>();
    previousMessages.forEach((message) => {
      const timelineEventId = typeof message.timelineEventId === 'string' ? message.timelineEventId.trim() : '';
      if (!timelineEventId) return;
      previousByTimelineEventId.set(timelineEventId, message);
    });

    nextMessages.forEach((message) => {
      const timelineEventId = typeof message.timelineEventId === 'string' ? message.timelineEventId.trim() : '';
      if (!timelineEventId) return;
      const timelineEvent = timelineRegistry[timelineEventId];
      const hasContinuation = message.scriptsInstances?.some(requiresListScriptPersistence);
      if ((!timelineEvent && !hasContinuation) || (timelineEvent && timelineEvent.category !== 'COMMUNICATION')) return;
      const previousMessage = previousByTimelineEventId.get(timelineEventId);
      if (!previousMessage) return;
      if (deepEqual(previousMessage, message)) return;
      if (hasContinuation) {
        void queueMessagePersistence(activeId, () => updateMessageTimelineEvent(timelineEventId, message));
      } else void updateMessageTimelineEvent(timelineEventId, message);
    });
  }, [
    applySessionMessageProjection,
    getChatSessionMessages,
    kernel,
    updateMessageTimelineEvent,
    queueMessagePersistence,
    writePendingSessionMessages,
  ]);

  const flushChatSession = useCallback((sessionId: string) => {
    const k = kernel.current;
    if (!k) return;

    const sessionToFlush = k.getInventory().ChatSession.registry[sessionId];
    if (!sessionToFlush) return;

    const originalSession = { ...sessionToFlush };
    const originalMessages = getChatSessionMessages(sessionId);

    const redo = () => {
      const k2 = kernel.current;
      if (!k2) return;

      k2.updateActiveSessionMessages(() => [], sessionId);
      const currentSession = k2.getInventory().ChatSession.registry[sessionId];
      if (currentSession) {
        upsertChatSessionDraft({
          ...currentSession,
          strategicPlan: null,
          updatedAt: new Date().toISOString(),
          isDirty: true,
        });
        k2.markChatSessionDirty?.(sessionId);
      }

      originalMessages.forEach((message) => {
        const timelineEventId = getMessageTimelineEventId(message);
        if (!timelineEventId || !isCommunicationTimelineEventId(timelineEventId)) return;
        void detachTimelineMessageEvent(timelineEventId);
      });
    };

    const undo = () => {
      const k2 = kernel.current;
      if (!k2) return;

      k2.updateActiveSessionMessages(() => originalMessages, sessionId);
      const currentSession = k2.getInventory().ChatSession.registry[sessionId];
      if (currentSession) {
        upsertChatSessionDraft({
          ...currentSession,
          strategicPlan: originalSession.strategicPlan ?? null,
          updatedAt: new Date().toISOString(),
          isDirty: true,
        });
        k2.markChatSessionDirty?.(sessionId);
      }

      const restoreAsync = async () => {
        for (const message of originalMessages) {
          const timelineEventId = getMessageTimelineEventId(message);
          if (!timelineEventId || !isCommunicationTimelineEventId(timelineEventId)) continue;
          const restoredCount = await restoreDetachedTimelineMessageEvent(
            sessionId,
            timelineEventId
          );
          if (restoredCount > 0) continue;

          const restoredEventId = await createMessageTimelineEvent({
            sessionId,
            message,
            eventType: toTimelineMessageEventType(message.role),
            targetEntityType: message.targetEntityType ?? null,
            targetEntityId: message.targetEntityId ?? null,
          });
          if (!restoredEventId || !kernel.current) continue;
          void kernel.current.syncEntity('TimelineEvent', restoredEventId).catch(() => undefined);
          kernel.current.updateActiveSessionMessages(
            (prevMessages) =>
              prevMessages.map((candidate) =>
                candidate.timestamp === message.timestamp &&
                candidate.role === message.role &&
                !candidate.timelineEventId
                  ? { ...candidate, timelineEventId: restoredEventId }
                  : candidate
              ),
            sessionId
          );
        }
      };

      void restoreAsync();
    };

    k.executeAction({
      description: `Flush chat: "${originalSession.name}"`,
      chatSessionId: sessionId,
      redo,
      undo,
    });
  }, [
    createMessageTimelineEvent,
    detachTimelineMessageEvent,
    getChatSessionMessages,
    isCommunicationTimelineEventId,
    kernel,
    restoreDetachedTimelineMessageEvent,
    upsertChatSessionDraft,
  ]);

  const addUserMessage = useCallback((content: string, images?: string[] | ImageAttachment[], options?: { technicalPrompt?: Promise<string> }) => {
    const k = kernel.current;
    if (!k) return;
    const activeId = k.getActiveChatSessionId();
    if (!activeId) return;

    const existingSession = k.getInventory().ChatSession.registry[activeId];
    if (!existingSession) return;
    const sessionSubject = isEntityFocusSubject(existingSession.activeSubject)
      ? existingSession.activeSubject
      : null;
    const fallbackProjectId = getLastProjectId(activeId);
    const targetEntityType = sessionSubject?.entityType ?? (fallbackProjectId ? 'Project' : null);
    const targetEntityId = sessionSubject?.entityId ?? fallbackProjectId ?? null;

    const userMessage: Message = {
      content,
      role: 'user',
      timestamp: new Date().toISOString(),
      ...(targetEntityType && targetEntityId
        ? {
            targetEntityType,
            targetEntityId,
          }
        : {}),
    };

    if (images && images.length > 0) {
      if (typeof images[0] === 'string') userMessage.imageUrls = images as string[];
      else userMessage.images = images as ImageAttachment[];
    }

    k.updateActiveSessionMessages((prevMessages) => [...prevMessages, userMessage], activeId);

    const userMessageTimestamp = userMessage.timestamp;
    const userId = k.getUserContext().dbUserId;
    const isList = Boolean(fallbackProjectId && k.getInventory().Project.registry[fallbackProjectId]?.projectType === 'LIST');
    const persist = async () => {
      // List Auto mode: the message shows as typed at once; the version with mentions, which the
      // agent and the server work from, is saved alongside it before the turn reads it back.
      const technicalPrompt = options?.technicalPrompt ? await options.technicalPrompt.catch(() => content) : content;
      if (technicalPrompt !== content) {
        userMessage.technicalPrompt = technicalPrompt;
        kernel.current?.updateActiveSessionMessages((prevMessages) => prevMessages.map((message) =>
          message.timestamp === userMessageTimestamp && message.role === 'user' ? { ...message, technicalPrompt } : message), activeId);
      }
      const createdEventId = await createMessageTimelineEvent({
        sessionId: activeId,
        message: userMessage,
        eventType: 'USER_MESSAGE',
        targetEntityType,
        targetEntityId,
        signal: isList ? AbortSignal.timeout(30_000) : undefined,
      });
      if (!createdEventId) {
        if (isList) throw new Error('Your message could not be saved. Please send it again.');
        return;
      }
      if (!kernel.current || kernel.current.getUserContext().dbUserId !== userId) return;
      void kernel.current.syncEntity('TimelineEvent', createdEventId).catch(() => undefined);
      kernel.current.updateActiveSessionMessages(
        (prevMessages) =>
          prevMessages.map((message) =>
            message.timestamp === userMessageTimestamp &&
            message.role === 'user' &&
            !message.timelineEventId
              ? { ...message, timelineEventId: createdEventId }
              : message
          ),
        activeId
      );
    };
    if (isList) void queueMessagePersistence(activeId, persist, true);
    else void persist();
  }, [createMessageTimelineEvent, getLastProjectId, kernel, queueMessagePersistence]);

  const persistVisibleMessage = useCallback(async (message: Message, targetSessionId?: string | null, options?: { requirePersistence?: boolean; signal?: AbortSignal }): Promise<void> => {
    const k = kernel.current;
    if (!k) return;
    const sessionId = (typeof targetSessionId === 'string' && targetSessionId.trim().length > 0)
      ? targetSessionId.trim()
      : k.getActiveChatSessionId();
    if (!sessionId || message.timelineEventId) return;

    const targetEntityType = message.targetEntityType ?? null;
    const targetEntityId = message.targetEntityId ?? null;
    const createdEventId = await createMessageTimelineEvent({
      sessionId,
      message,
      eventType: toTimelineMessageEventType(message.role),
      targetEntityType,
      targetEntityId,
      requirePersistence: options?.requirePersistence,
      signal: options?.signal,
    });
    if (!createdEventId && options?.requirePersistence) throw new Error('The response could not be saved. Your generation request is kept.');
    if (!createdEventId || !kernel.current) return;
    void kernel.current.syncEntity('TimelineEvent', createdEventId).catch(() => undefined);
    kernel.current.updateActiveSessionMessages(
      (prevMessages) =>
        prevMessages.map((existing) =>
          (message.clientMessageId
            ? existing.clientMessageId === message.clientMessageId
            : existing.timestamp === message.timestamp && existing.role === message.role) &&
          !existing.timelineEventId
            ? { ...existing, timelineEventId: createdEventId }
            : existing
        ),
      sessionId
    );
  }, [createMessageTimelineEvent, kernel]);

  // Persistently record an agent "telemetry" assistant message (e.g. "Enriched Decathlon")
  // into the chat. Mirrors addUserMessage's two-step persistence so these survive reload:
  // optimistic insert, then a server-side COMMUNICATION TimelineEvent whose id is backfilled
  // onto the message. The server-side emission is best-effort (try/catch) so a failure never
  // breaks the optimistic UX.
  const recordAgentTelemetry = useCallback((opts: {
    title: string;
    detail?: string;
    entityType?: string;
    entityId?: string;
    sessionId?: string | null;
  }) => {
    const k = kernel.current;
    if (!k) return;

    const title = typeof opts?.title === 'string' ? opts.title.trim() : '';
    if (!title) return;

    const activeId = (typeof opts?.sessionId === 'string' && opts.sessionId.trim().length > 0)
      ? opts.sessionId.trim()
      : k.getActiveChatSessionId();
    if (!activeId) return;

    const detail = typeof opts?.detail === 'string' ? opts.detail.trim() : '';
    const content = detail ? `${title}\n${detail}` : title;

    const entityType = typeof opts?.entityType === 'string' ? opts.entityType.trim() : '';
    const entityId = typeof opts?.entityId === 'string' ? opts.entityId.trim() : '';
    const targetEntityType = entityType && entityId ? entityType : null;
    const targetEntityId = entityType && entityId ? entityId : null;

    const assistantMessage: Message = {
      role: 'assistant',
      content,
      timestamp: new Date().toISOString(),
      agentName: 'Sourcing',
      ...(targetEntityType && targetEntityId
        ? { targetEntityType, targetEntityId }
        : {}),
    };

    k.updateActiveSessionMessages((prevMessages) => [...prevMessages, assistantMessage], activeId);

    try {
      const assistantMessageTimestamp = assistantMessage.timestamp;
      void createMessageTimelineEvent({
        sessionId: activeId,
        message: assistantMessage,
        eventType: toTimelineMessageEventType(assistantMessage.role),
        targetEntityType,
        targetEntityId,
      }).then((createdEventId) => {
        if (!createdEventId || !kernel.current) return;
        void kernel.current.syncEntity('TimelineEvent', createdEventId).catch(() => undefined);
        kernel.current.updateActiveSessionMessages(
          (prevMessages) =>
            prevMessages.map((message) =>
              message.timestamp === assistantMessageTimestamp &&
              message.role === 'assistant' &&
              !message.timelineEventId
                ? { ...message, timelineEventId: createdEventId }
                : message
            ),
          activeId
        );
      }).catch(() => undefined);
    } catch {
      /* persistence is best-effort, never break the optimistic UX */
    }
  }, [createMessageTimelineEvent, kernel]);

  // Add an assistant message AND persist it as a durable COMMUNICATION timeline
  // event, so it survives reload / navigating away and back (unlike
  // addAssistantMessage, which only writes a runtime pendingMessage). Used for the
  // Master's generated-image deliverable so produced images don't disappear.
  const addPersistedAssistantMessage = useCallback(async (
    content: string,
    agentName?: string,
    targetSessionId?: string | null,
  ): Promise<void> => {
    const k = kernel.current;
    if (!k) return;
    const sessionId = (typeof targetSessionId === 'string' && targetSessionId.trim().length > 0)
      ? targetSessionId.trim()
      : k.getActiveChatSessionId();
    if (!sessionId) return;

    const message: Message = {
      role: 'assistant',
      content,
      timestamp: new Date().toISOString(),
      ...(agentName ? { agentName } : {}),
    };
    k.updateActiveSessionMessages((prevMessages) => [...prevMessages, message], sessionId);

    try {
      const createdEventId = await createMessageTimelineEvent({
        sessionId,
        message,
        eventType: toTimelineMessageEventType(message.role),
      });
      if (createdEventId && kernel.current) {
        void kernel.current.syncEntity('TimelineEvent', createdEventId).catch(() => undefined);
        kernel.current.updateActiveSessionMessages(
          (prevMessages) =>
            prevMessages.map((m) =>
              m.timestamp === message.timestamp && m.role === 'assistant' && !m.timelineEventId
                ? { ...m, timelineEventId: createdEventId }
                : m
            ),
          sessionId
        );
      }
    } catch {
      /* persistence is best-effort, never break the optimistic UX */
    }
  }, [createMessageTimelineEvent, kernel]);

  // Auto-title a session with an intelligent LLM-generated name (like ChatGPT/
  // Claude), replacing the default "New chat" once there's real content. Only
  // touches default-named sessions, once, and re-checks the name before writing so
  // a manual rename is never clobbered.
  const maybeAutoTitleSession = useCallback(async (sessionId: string, options?: { force?: boolean }): Promise<void> => {
    const k = kernel.current;
    const id = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (!k || !id || AUTO_TITLING_IN_FLIGHT.has(id)) return;

    const session = k.getInventory().ChatSession.registry[id];
    // Normally only (re)title a still-default name; `force` re-derives the title from the
    // conversation even when the session already has a name (used to re-title all).
    if (!session || (!options?.force && !isDefaultSessionName(session.name))) return;

    const messages = getChatSessionMessages(id).filter((m) => m.role === 'user' || m.role === 'assistant');
    const hasUserContent = messages.some(
      (m) => m.role === 'user' && typeof m.content === 'string' && m.content.trim().length > 0
    );
    // A non-active session has a messageCount but its transcript is NOT hydrated
    // client-side, so retroactive titling of an old session sends just the id and
    // lets the server read the transcript from the DB. Truly-empty sessions bail.
    const hasStoredContent = (session.messageCount ?? 0) > 0;
    if (!hasUserContent && !hasStoredContent) return;

    AUTO_TITLING_IN_FLIGHT.add(id);
    try {
      const requestBody = hasUserContent
        ? {
            messages: messages
              .slice(0, 8)
              .map((m) => ({ role: m.role, content: typeof m.content === 'string' ? m.content : '' })),
          }
        : { sessionId: id };
      const res = await fetch('/api/ai/suggest-session-title', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      if (!res.ok) return;
      const data = await res.json();
      const title = typeof data?.title === 'string' ? data.title.trim() : '';
      if (!title || title === 'New conversation') return;
      // Re-check the name hasn't been changed (by the user or a prior title) before writing.
      const current = kernel.current?.getInventory().ChatSession.registry[id];
      if (current && (options?.force || isDefaultSessionName(current.name))) {
        kernel.current?.renameChatSession(id, title);
      }
    } catch {
      /* best-effort titling, never break the turn */
    } finally {
      AUTO_TITLING_IN_FLIGHT.delete(id);
    }
  }, [kernel, getChatSessionMessages]);

  const deleteMessage = useCallback((sessionId: string, messageIdentifier: string | number) => {
    const k = kernel.current;
    if (!k) return;

    const session = k.getInventory().ChatSession.registry[sessionId];
    if (!session) return;

    const currentMessages = getChatSessionMessages(sessionId);
    const messageIndex = currentMessages.findIndex(
      (message) => message.timestamp === messageIdentifier || message.timelineEventId === messageIdentifier
    );
    if (messageIndex === -1) return;
    const messageToDelete = currentMessages[messageIndex];
    if (!messageToDelete) return;

    const redo = () => {
      const k2 = kernel.current;
      if (!k2) return;
      k2.updateActiveSessionMessages(
        (messages) => messages.filter(
          (message) => message.timestamp !== messageIdentifier && message.timelineEventId !== messageIdentifier
        ),
        sessionId
      );
      const timelineEventId = getMessageTimelineEventId(messageToDelete);
      if (timelineEventId && isCommunicationTimelineEventId(timelineEventId)) {
        void detachTimelineMessageEvent(timelineEventId);
      }
    };

    const undo = () => {
      const k2 = kernel.current;
      if (!k2) return;
      k2.updateActiveSessionMessages((messages) => {
        const next = [...messages];
        next.splice(messageIndex, 0, messageToDelete);
        return next;
      }, sessionId);

      const restoredMessageTimestamp = messageToDelete.timestamp;
      const restoreDetachedEvent = async () => {
        const timelineEventId = getMessageTimelineEventId(messageToDelete);
        const restoredCount = timelineEventId && isCommunicationTimelineEventId(timelineEventId)
          ? await restoreDetachedTimelineMessageEvent(sessionId, timelineEventId)
          : 0;
        if (restoredCount > 0) return;

        const restoredEventId = await createMessageTimelineEvent({
          sessionId,
          message: messageToDelete,
          eventType: toTimelineMessageEventType(messageToDelete.role),
          targetEntityType: (() => {
            const subject = isEntityFocusSubject(session.activeSubject) ? session.activeSubject : null;
            return (
              messageToDelete.targetEntityType ??
              subject?.entityType ??
              (getLastProjectId(sessionId) ? 'Project' : null)
            );
          })(),
          targetEntityId: (() => {
            const subject = isEntityFocusSubject(session.activeSubject) ? session.activeSubject : null;
            return (
              messageToDelete.targetEntityId ??
              subject?.entityId ??
              getLastProjectId(sessionId) ??
              null
            );
          })(),
        });
        if (!restoredEventId || !kernel.current) return;
        void kernel.current.syncEntity('TimelineEvent', restoredEventId).catch(() => undefined);
        kernel.current.updateActiveSessionMessages(
          (prevMessages) =>
            prevMessages.map((message) =>
              message.timestamp === restoredMessageTimestamp &&
              message.role === messageToDelete.role &&
              !message.timelineEventId
                ? { ...message, timelineEventId: restoredEventId }
                : message
            ),
          sessionId
        );
      };

      void restoreDetachedEvent();
    };

    k.executeAction({
      description: `Delete message: "${String(messageToDelete?.content ?? '').substring(0, 20)}..."`,
      chatSessionId: sessionId,
      redo,
      undo,
    });
  }, [
    createMessageTimelineEvent,
    detachTimelineMessageEvent,
    getChatSessionMessages,
    getLastProjectId,
    isCommunicationTimelineEventId,
    kernel,
    restoreDetachedTimelineMessageEvent,
  ]);

  const openChatMessageMenu = useCallback<useChatReturnType['openChatMessageMenu']>(({ position, ...input }) => {
    const k = kernel.current;
    if (!k) return;
    const actions = buildChatMessageMenuActions(k, {
      ...input,
      sessionId: k.getActiveChatSessionId(),
      isFaterOrgMember: Boolean(k.getUserContext()?.isFaterOrgMember),
    });
    k.setMenuData({ position, actions });
  }, [kernel]);

  const showChatAttachmentToAgent = useCallback<useChatReturnType['showChatAttachmentToAgent']>(async (url, show) => {
    const k = kernel.current;
    const sessionId = k?.getActiveChatSessionId();
    if (!k || !sessionId) return;
    const projectId = getLastProjectId(sessionId) || resolveProjectIdFromChatSessionContext(k.getInventory().ChatSession.registry[sessionId] ?? null);
    if (!projectId) return;
    k.logUserAction(show ? 'Chat Attachment Shown To Agent' : 'Chat Attachment Hidden From Agent', { session_id: sessionId, url });
    try {
      const response = await fetch('/api/chat/attachments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId, sessionId, url, show }),
      });
      const answer = await response.json().catch(() => null) as { message?: unknown } | null;
      if (!response.ok || typeof answer?.message === 'string') {
        k.handleError(typeof answer?.message === 'string' ? answer.message : 'Could not change what the agent sees.', { logToConsole: !response.ok });
      }
    } catch {
      k.handleError('Could not change what the agent sees.');
    }
  }, [getLastProjectId, kernel]);

  const buildSessionAutosavePayload = useCallback((sessionId: string): PersistedChatSessionPayload | null => {
    const k = kernel.current;
    if (!k) return null;
    const session = k.getInventory().ChatSession.registry[sessionId];
    if (!session) return null;

    const messages = getChatSessionMessages(sessionId);
    const metadata = buildSessionMessageMetadata(messages, session.lastMessageSnippet ?? null);

    return toPersistedChatSessionPayload({
      ...(session as ChatSessionInventoryEntityState),
      ...metadata,
    });
  }, [getChatSessionMessages, kernel]);

  const seedSessionAutosaveBaseline = useCallback((sessionId: string) => {
    const k = kernel.current;
    if (!k) return;
    const payload = buildSessionAutosavePayload(sessionId);
    if (!payload) return;
    k.setAutosaveBaseline(
      'ChatSession',
      sessionId,
      JSON.stringify(payload),
      { scope: CHAT_SESSION_DTO_AUTOSAVE_SCOPE, saveState: 'saved' }
    );
  }, [buildSessionAutosavePayload, kernel]);

  const persistGlobalSessionToServer = useCallback(async (
    sessionId: string,
    payloadOverride?: PersistedChatSessionPayload | null
  ) => {
    if (!kernel.current || !sessionId) return;
    const localDraft = kernel.current.readEntityKernelState('ChatSession', sessionId);
    if (isRecord(localDraft) && localDraft[LOCAL_ENTITY_DELETE_MARKER] === true) {
      return;
    }
    const currentSession = kernel.current.getInventory().ChatSession.registry[sessionId];
    if (!currentSession) return;

    upsertChatSessionDraft({ ...currentSession, saveStatus: 'saving' });

    try {
      const sessionPayload =
        payloadOverride ?? buildSessionAutosavePayload(sessionId) ?? toPersistedChatSessionPayload(currentSession);
      const updatePayload: Record<string, unknown> = { ...sessionPayload };
      delete updatePayload.id;
      await kernel.current.mutateInventory([{
        action: 'UPDATE',
        entity: 'ChatSession',
        id: sessionId,
        payload: updatePayload,
      }], `Persist ChatSession DTO ${sessionId}`);

      await kernel.current.syncEntity('ChatSession', sessionId).catch(() => undefined);
      const latestSession = kernel.current.getInventory().ChatSession.registry[sessionId] ?? currentSession;
      const latestMessages = getChatSessionMessages(sessionId);
      const latestMessageMetadata = buildSessionMessageMetadata(
        latestMessages,
        latestSession.lastMessageSnippet ?? null
      );

      const merged: ChatSession = {
        ...latestSession,
        ...latestMessageMetadata,
        strategicPlan: latestSession.strategicPlan ?? currentSession.strategicPlan ?? null,
        activeSubject: latestSession.activeSubject ?? currentSession.activeSubject ?? null,
        subjectHistory: latestSession.subjectHistory ?? currentSession.subjectHistory,
        workingMemory: latestSession.workingMemory ?? currentSession.workingMemory,
        id: sessionId,
        isDirty: false,
        saveStatus: 'saved' as const,
      };
      upsertChatSessionDraft(merged);
      seedSessionAutosaveBaseline(sessionId);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        try {
          const latestSession = kernel.current.getInventory().ChatSession.registry[sessionId];
          if (!latestSession) return;
          const recreatedResponse = await axios.post<ChatSession>(
            '/api/inventory/sessions',
            {
              name: latestSession.name || DEFAULT_GLOBAL_SESSION_NAME,
              session: toPersistedChatSessionPayload(latestSession),
              // Recreated sessions keep the dead session's immutable home.
              creationScope: {
                module: latestSession.creationModule ?? 'global',
                entityType: latestSession.creationEntityType ?? null,
                entityId: latestSession.creationEntityId ?? null,
              },
            },
            { withCredentials: true }
          );
          const recreatedSession: ChatSession = {
            ...recreatedResponse.data,
            isDirty: false,
            saveStatus: 'saved',
          };
          const wasActive = kernel.current.getActiveChatSessionId() === sessionId;
          cancelQueuedSessionSave(sessionId);
          removeChatSessionDraft(sessionId);
          upsertChatSessionDraft(recreatedSession);
          if (wasActive) {
            setActiveChatSessionIdInternal(recreatedSession.id);
          }
          return;
        } catch (recreateError) {
          console.error('[useChat] Failed to recreate missing session after 404:', recreateError);
        }
      }
      console.error('[useChat] Failed to persist session:', error);
      const latestSession = kernel.current.getInventory().ChatSession.registry[sessionId];
      if (!latestSession) return;
      upsertChatSessionDraft({ ...latestSession, saveStatus: 'error' });
    }
  }, [
    buildSessionAutosavePayload,
    cancelQueuedSessionSave,
    getChatSessionMessages,
    kernel,
    removeChatSessionDraft,
    setActiveChatSessionIdInternal,
    seedSessionAutosaveBaseline,
    upsertChatSessionDraft,
  ]);

  const enqueueGlobalSessionSave = useCallback((sessionId: string) => {
    const k = kernel.current;
    if (!k) return;
    const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (!normalizedSessionId) return;

    k.queueAutosave<PersistedChatSessionPayload>({
      entityType: 'ChatSession',
      entityId: normalizedSessionId,
      scope: CHAT_SESSION_DTO_AUTOSAVE_SCOPE,
      reason: 'ChatSession DTO autosave',
      debounceMs: CHAT_SESSION_DTO_AUTOSAVE_DEBOUNCE_MS,
      maxRetries: 1,
      buildPayload: () => buildSessionAutosavePayload(normalizedSessionId),
      persist: async (payload) => {
        const liveSession = kernel.current?.getInventory().ChatSession.registry[normalizedSessionId];
        if (!liveSession || !liveSession.isDirty) {
          return true;
        }
        await persistGlobalSessionToServer(normalizedSessionId, payload);
        return true;
      },
    });
  }, [buildSessionAutosavePayload, kernel, persistGlobalSessionToServer]);

  const scheduleGlobalSessionSave = useCallback((sessionId: string) => {
    enqueueGlobalSessionSave(sessionId);
  }, [enqueueGlobalSessionSave]);

  const markChatSessionDirty = useCallback((sessionId: string) => {
    if (!kernel.current) return;
    const session = kernel.current.getInventory().ChatSession.registry[sessionId];
    if (!session) return;

    upsertChatSessionDraft({
      ...session,
      isDirty: true,
      saveStatus: session.saveStatus === 'saving' ? 'saving' : 'idle',
    });
    scheduleGlobalSessionSave(sessionId);
  }, [kernel, scheduleGlobalSessionSave, upsertChatSessionDraft]);

  const setSessionCollaborationMode = useCallback((sessionId: string, mode: CollaborationMode) => {
    const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (!normalizedSessionId) return;
    kernel.current?.mergeEntityKernelState('ChatSession', normalizedSessionId, {
      collaborationMode: mode,
      updatedAt: new Date().toISOString(),
    });
    kernel.current?.markChatSessionDirty?.(normalizedSessionId);
    kernel.current?.logUserAction('Session Collaboration Mode Changed', {
      sessionId: normalizedSessionId,
      mode,
    });
  }, [kernel]);

  const persistChatSession = useCallback(async (sessionId: string) => {
    if (!kernel.current) return;
    const session = kernel.current.getInventory().ChatSession.registry[sessionId];
    if (!session) return;
    
    upsertChatSessionDraft({ ...session, isDirty: true });
    enqueueGlobalSessionSave(sessionId);
    await kernel.current.flushAutosave('ChatSession', sessionId, CHAT_SESSION_DTO_AUTOSAVE_SCOPE);
  }, [kernel, enqueueGlobalSessionSave, upsertChatSessionDraft]);

  const activateChatSession = useCallback(async (
    sessionId: string,
    options?: { openingCampaignId?: string; isStillWanted?: () => boolean },
  ) => {
    if (!kernel.current) return;
    const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (!normalizedSessionId) return;

    // Hydrate the transcript with two bounded passes instead of one. A single
    // all-category `asc` page silently drops the conversation on busy sessions:
    // generation-heavy (List) projects emit dozens of MUTATION rows per turn, so
    // once a session passes the 500-row cap the oldest page is all machine events
    // and every recent user/assistant message falls outside the window. Pulling
    // COMMUNICATION separately guarantees the messages are always fetched, and
    // going `desc` keeps the *recent* activity rather than the first ever page.
    const [syncedSession] = await Promise.all([
      kernel.current.syncEntity('ChatSession', sessionId),
      kernel.current.refreshInventory(
        'TimelineEvent',
        { chatSessionId: sessionId, category: 'COMMUNICATION', sortDir: 'desc' },
        500,
        { throwOnError: false }
      ).catch(() => undefined),
      kernel.current.refreshInventory(
        'TimelineEvent',
        { chatSessionId: sessionId, sortDir: 'desc' },
        500,
        { throwOnError: false }
      ).catch(() => undefined),
    ]);

    if (options?.isStillWanted?.() === false) return;

    const liveSession = kernel.current.getInventory().ChatSession.registry[normalizedSessionId] ?? null;
    if (!syncedSession || !liveSession) {
      console.warn('[useChat] activateChatSession skipped: session not found or inaccessible.', {
        requestedSessionId: normalizedSessionId,
      });
      return;
    }

    syncSessionMessagesFromTimelineRegistry(normalizedSessionId);
    seedSessionAutosaveBaseline(normalizedSessionId);
    setActiveChatSessionIdInternal(normalizedSessionId);
    const openedProjectId = resolveProjectIdFromChatSessionContext(liveSession);
    if (openedProjectId) restoreProjectToolView(kernel.current, openedProjectId);
    const openedProject = openedProjectId ? kernel.current.getInventory().Project.registry[openedProjectId] : null;
    const openedView = openedProjectId ? kernel.current.readProjectRuntimeState(openedProjectId).projectViewMode : null;
    kernel.current.setStudioSection(resolveProjectStudioSection(openedProject?.projectType, openedView, kernel.current.getUserContext().permissions));

    // THE PAGE FOLLOWS THE CONVERSATION (the reverse half of the campaign<->chat binding; the
    // forward half is openSourceCampaign). A Source conversation's subject says which campaign it
    // belongs to; activating it must put that campaign on screen, and activating a Source
    // conversation bound to NO campaign must put the dashboard on screen. Without this, switching
    // conversations left the previous page up, which is exactly the "campaign A's shortlist next
    // to campaign B's chat" bug. Non-Source conversations leave Source state untouched.
    const activatedSubject = liveSession.activeSubject ?? null;
    const isSourceConversation =
      liveSession.creationModule === 'source' ||
      (isEntityFocusSubject(activatedSubject) &&
        activatedSubject.focusActionType === 'ORCHESTRATE_SOURCING');
    if (isSourceConversation) {
      const boundCampaignId =
        isEntityFocusSubject(activatedSubject) && typeof activatedSubject.campaignId === 'string'
          ? activatedSubject.campaignId
          : null;
      const currentSelection = kernel.current.getState().sourceSelectedCampaignId ?? null;
      if (boundCampaignId && currentSelection !== boundCampaignId) {
        // No scope to state alongside the id any more: what the run covered is read off its own
        // row (resolveOpenCampaignScope), so the selection can no longer be looked up in the wrong
        // registry. Entering a bound conversation IS navigation into its campaign, so the draft
        // flag drops like it does on any other campaign open.
        kernel.current.setState({
          sourceSelectedCampaignId: boundCampaignId,
          sourceFocusedFitId: null,
          sourceSelectedFitIds: [],
          sourceCreatingCampaign: false,
        });
        // At boot (URL-restored session) no registry holds this campaign yet; fetch it so the
        // shortlist header, status and aura have a row to read. Fire-and-forget: the view
        // renders from the selection either way.
        //
        // THE UNIFIED ENDPOINT, because at this point we do not know which family this campaign
        // belongs to - that is precisely what the registry we are about to fill would tell us.
        // Asking for an ASSET campaign was a guess, and it was wrong for every portfolio run: a
        // 404, no row, and so no header, no status and no aura on exactly the page that needed
        // them. `Campaign` answers for both families and fills the slice `resolveOpenCampaignScope`
        // reads, so the scope resolves off this same call instead of falling back to 'asset'.
        void kernel.current
          .syncEntity('Campaign', boundCampaignId)
          .catch(() => undefined);
      } else if (!boundCampaignId && currentSelection && currentSelection !== options?.openingCampaignId) {
        // An unbound Source conversation shows the dashboard. sourceCreatingCampaign is
        // deliberately left alone: the new-campaign draft is itself an unbound conversation,
        // and clearing the flag here would throw away the half-filled setup form on a mere
        // conversation switch.
        //
        // EXCEPT WHEN THIS ACTIVATION *IS* THE CAMPAIGN OPENING. openSourceCampaign selects the
        // campaign, resolves its conversation, then activates it - and lands here. `boundCampaignId`
        // is read off the ChatSession ROW in the local registry, which is not always current: a
        // conversation just created by that resolve, or a row that predates the binding, reads as
        // unbound. This branch then "corrected" the page back to the dashboard, closing the very
        // campaign the user had just clicked. That is the bug where entering a campaign bounces
        // straight back out, and it is intermittent because it depends on how fresh the row is.
        //
        // The opener says what it is doing rather than leaving this half to infer it from data
        // that may be stale. Every other path - switching to a genuinely unbound Source
        // conversation - passes no id and still lands on the dashboard.
        // AND NOW IT DOES NOTHING, DELIBERATELY.
        //
        // Everything above is an argument against this branch. It decided to close the user's
        // open shortlist by reading `boundCampaignId` off a local ChatSession row that, by its
        // own admission two paragraphs up, "is not always current". A stale read here does not
        // degrade gracefully: it throws someone out of a project they are working in and drops
        // them on the dashboard, mid-task, for no reason they can see. The escape hatch added
        // last time (openingCampaignId) only covered the ONE activation path that had been
        // reported; every other re-activation of the same session - an agent turn, a socket
        // update, a refresh landing before the row does - could still do it.
        //
        // Closing a shortlist is a USER action. It happens when they press back, open another
        // campaign, or delete this one - all of which clear the selection explicitly elsewhere.
        // No heuristic about a possibly-stale row gets to make that decision for them.
        //
        // The cost of leaving it: switching to a genuinely unbound Source conversation keeps the
        // previous shortlist on screen instead of showing the dashboard. That is a stale view the
        // user can fix with one click, against a lost place in their work. Not a close call.
      }
    }
  }, [kernel, seedSessionAutosaveBaseline, setActiveChatSessionIdInternal, syncSessionMessagesFromTimelineRegistry]);

  const createAndActivateSession = useCallback(async (
    options?: {
      isStillWanted?: () => boolean;
      nameHint?: string;
      initialSubject?: ActiveEntityFocus | null;
      creationScope?: SessionCreationScope | null;
      collaborationMode?: CollaborationMode;
    }
  ) => {
    if (!kernel.current) return null;
    const trimmedHint = options?.nameHint?.trim();
    const preferredName = trimmedHint && trimmedHint.length > 0 ? trimmedHint.slice(0, 120) : DEFAULT_GLOBAL_SESSION_NAME;
    const tempId = uuidv4();
    const createdAt = new Date().toISOString();
    const defaultSessionCollaborationMode = options?.collaborationMode ?? resolveSessionDefaultCollaborationMode();
    const initialSubject = normalizeInitialSubject(options?.initialSubject);
    const creationScope =
      options?.creationScope ?? deriveCreationScopeFromSubject(initialSubject);
    const localSessionSeed: ChatSessionInventoryEntityState = {
      id: tempId,
      name: preferredName,
      ownerId: kernel.current.getUserContext().dbUserId ?? null,
      messageCount: 0,
      strategicPlan: null,
      collaborationMode: defaultSessionCollaborationMode,
      activeSubject: initialSubject,
      subjectHistory: initialSubject
        ? [toSubjectFocusHistoryEntry(initialSubject, createdAt)]
        : [],
      creationModule: creationScope.module,
      creationEntityType: creationScope.entityType ?? null,
      creationEntityId: creationScope.entityId ?? null,
      createdAt,
      updatedAt: createdAt,
    };

    try {
      const createPayload = toPersistedChatSessionPayload(localSessionSeed);
      const createMutationPayload: Record<string, unknown> = { ...createPayload };
      delete createMutationPayload.id;
      createMutationPayload.creationModule = creationScope.module;
      createMutationPayload.creationEntityType = creationScope.entityType ?? null;
      createMutationPayload.creationEntityId = creationScope.entityId ?? null;

      const result = await kernel.current.mutateInventory([{
        action: 'CREATE',
        entity: 'ChatSession',
        tempId: `chat-session-ensure-${tempId}`,
        payload: createMutationPayload,
      }], 'Create and activate chat session');

      const createdSessionIdRaw = result[0]?.id;
      const realId =
        typeof createdSessionIdRaw === 'string' ? createdSessionIdRaw.trim() : '';
      if (!realId) {
        throw new Error('ChatSession create mutation did not return an id.');
      }

      await kernel.current.syncEntity('ChatSession', realId).catch(() => undefined);
      const canonicalCreatedSession =
        kernel.current.getInventory().ChatSession.registry[realId] ?? null;

      const session: ChatSession = canonicalCreatedSession
        ? { ...canonicalCreatedSession, isDirty: false, saveStatus: 'saved' }
        : {
            ...localSessionSeed,
            id: realId,
            isDirty: false,
            saveStatus: 'saved',
          };

      upsertChatSessionDraft(session);
      seedSessionAutosaveBaseline(session.id);
      if (options?.isStillWanted?.() === false) return session.id;
      setActiveChatSessionIdInternal(session.id);
      if (initialSubject) {
        kernel.current.setActiveSubject(session.id, initialSubject);
      }

      return session.id;
    } catch (error) {
      console.error('[useChat] Failed to create chat session', error);
      return null;
    }
  }, [kernel, resolveSessionDefaultCollaborationMode, seedSessionAutosaveBaseline, setActiveChatSessionIdInternal, upsertChatSessionDraft]);

  // One creation at a time, shared by everyone who asks while it runs. Two callers routinely ask
  // for "the" session in the same tick on a fresh load (portal navigation and the list-focus
  // handler both do), both read the active id as still empty, and both minted one: 33 duplicate
  // pairs in a single day across 10 users, every one of them created within the same millisecond
  // and never written to. The active id only exists once creation resolves, so it cannot be the
  // guard, and the guard has to live here rather than in each caller: the callers do not know
  // about each other.
  const sessionCreationInFlight = useRef<Promise<string | null> | null>(null);

  const ensureSession = useCallback(async (
    options?: {
      isStillWanted?: () => boolean;
      nameHint?: string;
      initialSubject?: ActiveEntityFocus | null;
      creationScope?: SessionCreationScope | null;
    }
  ) => {
    if (!kernel.current || options?.isStillWanted?.() === false) return null;
    const activeId = kernel.current.getActiveChatSessionId();
    const initialSubject = normalizeInitialSubject(options?.initialSubject);
    if (activeId) {
      const activeSession = kernel.current.getInventory().ChatSession.registry[activeId];
      if (activeSession) {
        if (initialSubject) {
          kernel.current.setActiveSubject(activeSession.id, initialSubject);
        }
        return activeSession.id;
      }
    }

    const pending = sessionCreationInFlight.current;
    if (pending) {
      const sharedId = await pending;
      if (options?.isStillWanted?.() === false) return sharedId;
      // A failed shared attempt is not an answer: fall through and make our own rather than
      // report "no session" to a caller that never got one tried on its behalf.
      if (sharedId) {
        if (kernel.current?.getActiveChatSessionId() !== sharedId) {
          await activateChatSession(sharedId, { isStillWanted: options?.isStillWanted });
        }
        if (options?.isStillWanted?.() === false) return sharedId;
        if (initialSubject) {
          kernel.current?.setActiveSubject(sharedId, initialSubject);
        }
        return sharedId;
      }
    }

    const creation = createAndActivateSession({
      ...options,
      initialSubject,
    });
    sessionCreationInFlight.current = creation;
    try {
      return await creation;
    } finally {
      if (sessionCreationInFlight.current === creation) {
        sessionCreationInFlight.current = null;
      }
    }
  }, [kernel, activateChatSession, createAndActivateSession]);

  const emitSessionTimelineEvent = useCallback(({
    sessionId,
    category,
    eventType,
    targetEntityType,
    targetEntityId,
    payload,
  }: {
    sessionId: string;
    category: 'TELEMETRY' | 'AGENTIC';
    eventType: string;
    targetEntityType?: string | null;
    targetEntityId?: string | null;
    payload: Record<string, unknown>;
  }) => {
    const k = kernel.current;
    if (!k) return;
    const projectId = getLastProjectId(sessionId);
    const resolvedTargetEntityType =
      targetEntityType === undefined ? (projectId ? 'Project' : null) : targetEntityType;
    const resolvedTargetEntityId =
      targetEntityId === undefined ? (projectId || null) : targetEntityId;
    void k.mutateInventory([{
      action: 'CREATE',
      entity: 'TimelineEvent',
      tempId: `timeline-event-session-${uuidv4()}`,
      payload: {
        chatSessionId: sessionId,
        ...(resolvedTargetEntityType ? { targetEntityType: resolvedTargetEntityType } : {}),
        ...(resolvedTargetEntityId ? { targetEntityId: resolvedTargetEntityId } : {}),
        category,
        eventType,
        payload,
      },
    }], `Create session timeline event ${eventType} for session ${sessionId}`).catch((error) => {
      console.error('[useChat] Failed to emit session timeline event through inventory mutate', {
        sessionId,
        eventType,
        error,
      });
    });
  }, [getLastProjectId, kernel]);

  const anchorEntity = useCallback((sessionId: string, entityType: EntityType, entityId: string, mass: number = 1) => {
    const k = kernel.current;
    if (!k) return;
    const session = k.getInventory().ChatSession.registry[sessionId];
    if (!session) return;

    const newAnchor: ContextAnchor = {
      entityType,
      entityId,
      mass,
      provenance: 'Manual anchor from chat UI.',
      lastAccessedAt: Date.now()
    };

    const currentMemory: WorkingMemory = session.workingMemory || { anchors: [] };
    const anchorMap = new Map(currentMemory.anchors.map(a => [`${a.entityType}:${a.entityId}`, a]));
    anchorMap.set(`${entityType}:${entityId}`, newAnchor);

    const newMemory: WorkingMemory = { ...currentMemory, anchors: Array.from(anchorMap.values()) };
    k.mergeEntityKernelState('ChatSession', sessionId, { workingMemory: newMemory });
    k.markChatSessionDirty?.(sessionId);
    emitSessionTimelineEvent({
      sessionId,
      category: 'AGENTIC',
      eventType: 'ENTITY_ANCHORED',
      targetEntityType: entityType,
      targetEntityId: entityId,
      payload: {
        entityType,
        entityId,
        mass,
        provenance: newAnchor.provenance ?? null,
      },
    });
  }, [emitSessionTimelineEvent, kernel]);

  const unanchorEntity = useCallback((sessionId: string, entityType: EntityType, entityId: string) => {
    const k = kernel.current;
    if (!k) return;
    const session = k.getInventory().ChatSession.registry[sessionId];
    if (!session || !session.workingMemory) return;

    const nextAnchors = session.workingMemory.anchors.filter(
      a => !(a.entityType === entityType && a.entityId === entityId)
    );

    if (nextAnchors.length === session.workingMemory.anchors.length) return;

    const newMemory: WorkingMemory = { ...session.workingMemory, anchors: nextAnchors };
    k.mergeEntityKernelState('ChatSession', sessionId, { workingMemory: newMemory });
    k.markChatSessionDirty?.(sessionId);
    emitSessionTimelineEvent({
      sessionId,
      category: 'AGENTIC',
      eventType: 'ENTITY_UNANCHORED',
      targetEntityType: entityType,
      targetEntityId: entityId,
      payload: {
        entityType,
        entityId,
      },
    });
  }, [emitSessionTimelineEvent, kernel]);

  /**
   * Drops the whole focus of a session: every anchor is unanchored one by one (each emits its own
   * ENTITY_UNANCHORED timeline event), then the subject cursor is cleared. The selected asset is
   * reset in every case, including when there is no active session, since it is the focus the chip
   * shows when no conversation is active.
   */
  const clearSessionFocus = useCallback((sessionId: string | null) => {
    const k = kernel.current;
    if (!k) return;
    if (sessionId) {
      const session = k.getInventory().ChatSession.registry[sessionId];
      for (const anchor of session?.workingMemory?.anchors ?? []) {
        k.unanchorEntity(sessionId, anchor.entityType, anchor.entityId);
      }
      k.setActiveSubject(sessionId, null);
    }
    k.setSelectedAssetId(null);
  }, [kernel]);

  /**
   * Flips the presentation mode of a table focus entry (focusRuntimeByKey['table:<view>']) without
   * touching the rest of it: the list stack publishes filters, pageSize and visibleIds there
   * (useTableFocusRuntimePublication), and a mode toggle must not wipe them.
   */
  const setSessionTableFocusPresentationMode = useCallback(({
    sessionId,
    tableView,
    entityType,
    presentationMode,
  }: {
    sessionId: string | null;
    tableView: SessionTableFocusView;
    entityType: EntityType;
    presentationMode: 'table' | 'grid';
  }) => {
    if (!sessionId) return;
    const k = kernel.current;
    if (!k) return;

    const runtimeMap = toFocusRuntimeMap(
      k.readEntityRuntimeState('ChatSession', sessionId).focusRuntimeByKey
    );
    const runtimeKey = `table:${tableView}`;
    const existingEntry = runtimeMap[runtimeKey];
    if (existingEntry?.presentationMode === presentationMode) return;

    k.patchEntityRuntimeState('ChatSession', sessionId, {
      focusRuntimeByKey: {
        ...runtimeMap,
        [runtimeKey]: {
          ...existingEntry,
          focusScope: 'table',
          entityType,
          tableView,
          presentationMode,
          updatedAt: Date.now(),
        },
      },
    });
  }, [kernel]);

  const setActiveSubject = useCallback((
    sessionId: string,
    subject: ActiveEntityFocus | null,
    options?: { recordInHistory?: boolean }
  ) => {
    const k = kernel.current;
    if (!k) return;

    const session = k.getInventory().ChatSession.registry[sessionId];
    if (!session) return;

    let normalizedIncoming = normalizeSubjectFocus(subject);
    const currentSubject = normalizeSubjectFocus(session.activeSubject);

    // A LEAD IS NEVER THE SUBJECT. Its detail already exists, expanded under its own row in the
    // shortlist, carrying the same summary, scoring, expansion, stores, revenue and contacts. As a
    // subject it became a full-width page that took the dashboard over and pushed the shortlist out
    // of view, so the agent answering /interesting replaced the list it had just ranked with one
    // row of it. Removed on the owner's instruction, 2026-08-16: "this focused view shouldn't
    // exist, we already have detail views in the shortlist."
    //
    // Redirected rather than refused, because a refusal here is a dead end: the agent asked for the
    // user's attention to move to that lead, and expanding its row is the honest way to give it.
    if (normalizedIncoming?.entityType === 'Lead' && normalizedIncoming.entityId) {
      // NEVER RETURN WITHOUT A SUBJECT. The first cut of this did, and it emptied the dashboard
      // within minutes: a session whose STORED subject is a lead - and there are such sessions, the
      // agent wrote them before this rule existed - restored to no subject at all, so the portfolio
      // scope resolved to nothing and the campaign list came back "No campaigns for this portfolio
      // yet". A redirection that can land nowhere is a dead end wearing a redirection's clothes.
      //
      // The lead's campaign IS the surface that shows the lead, so that is the subject, and the row
      // opens under it. When the campaign cannot be resolved the old behaviour stands: better the
      // page than a blank screen.
      const lead = k.getInventory().Lead.registry[normalizedIncoming.entityId] as
        { lastCampaignId?: string | null } | undefined;
      const campaignId = typeof lead?.lastCampaignId === 'string' ? lead.lastCampaignId.trim() : '';
      if (campaignId) {
        k.setState({ sourceFocusedFitId: normalizedIncoming.entityId });
        normalizedIncoming = normalizeSubjectFocus({ entityType: 'SourcingCampaign', entityId: campaignId });
      }
    }

    // A SUBJECT IS A NAVIGATION CURSOR, AND A WRITE HERE NEVER FAILS.
    //
    // A session holds no subject, one, or several: they stack in subjectHistory and the LAST one
    // drives what the interface shows. Ownership is a different field and always was: the
    // creationModule/creationEntityType/creationEntityId triple, immutable since it is written
    // (schema.postgres.prisma), is WHERE the conversation was born, and it is what focusEntityUI
    // resolves a project's or a campaign's own conversation by. Opening an entity that owns a
    // conversation SWITCHES session; it does not repoint this one.
    //
    // This function used to refuse any write that targeted a different anchor, to stop a sourcing
    // conversation drifting onto an Organisation. It made the mutable field do the immutable
    // field's job, and the refusal was a dead end rather than a redirection: a session parked on
    // `table:projects` (the normal end state of browsing the grid) could never leave it, and since
    // the entity page paints off the session subject, 245 projects simply would not open. Every
    // table row click went the same way, and goToPreviousSubject / goToNextSubject / the top-bar
    // arrows / FocusSelectorChip became inert by construction. Whatever state the app is in,
    // opening something must move the interface, never block it.
    //
    // THE CAMPAIGN BINDING IS STICKY ON ITS OWN ENTITY. Every subject write in the app funnels
    // through here, and most writers (focusEntityUI, SET_ACTIVE_SUBJECT, agent re-stamps) build
    // their subject from {entityType, entityId, focusActionType} alone - they know nothing about
    // campaign bindings. Without this merge, any same-entity re-assert would be a REAL write
    // (equivalence distinguishes campaignId) that silently strips the binding and persists the
    // stripped subject. A bound conversation only unbinds by genuinely moving to another entity.
    // OWNERSHIP IS PERMANENT; THE VIEW IS NOT. The campaign binding says "this conversation
    // belongs to campaign X", which stays true when the user looks at another asset. It used to be
    // preserved only for a same-entity re-assert, so picking a different asset in the Source top
    // bar dropped it and persisted the drop: the conversation was silently orphaned from its
    // campaign, and the campaign fell back to an empty chat on the next open. The binding now
    // survives any subject move and is only ever replaced by an explicit different campaignId.
    const normalizedSubject =
      normalizedIncoming &&
      currentSubject &&
      isEntityFocusSubject(normalizedIncoming) &&
      isEntityFocusSubject(currentSubject) &&
      !normalizedIncoming.campaignId &&
      currentSubject.campaignId
        ? { ...normalizedIncoming, campaignId: currentSubject.campaignId }
        : normalizedIncoming;

    const isUnchanged =
      (!currentSubject && !normalizedSubject) ||
      areSubjectFocusesEquivalent(currentSubject, normalizedSubject);
    if (isUnchanged) return;

    // Focus changes are event-driven; keep ChatSession DTO autosave for true metadata edits.
    const shouldRecordInHistory = options?.recordInHistory !== false;

    if (normalizedSubject) {
      const existingHistory = Array.isArray(session.subjectHistory) ? session.subjectHistory : [];
      const runtime = k.readEntityRuntimeState('ChatSession', sessionId);
      const runtimeNavigationHistory = Array.isArray(runtime.subjectNavigationHistory)
        ? runtime.subjectNavigationHistory
        : [];
      const navigationHistorySource =
        runtimeNavigationHistory.length > 0 ? runtimeNavigationHistory : existingHistory;
      const navigationHistory = navigationHistorySource.slice(-MAX_SUBJECT_HISTORY_SIZE);
      const currentKey = getSubjectKey(currentSubject);
      const runtimeCursor =
        typeof runtime.subjectHistoryCursor === 'number' && Number.isFinite(runtime.subjectHistoryCursor)
          ? Math.floor(runtime.subjectHistoryCursor)
          : null;
      const resolveNavigationCursor = (): number | null => {
        if (
          runtimeCursor !== null &&
          runtimeCursor >= 0 &&
          runtimeCursor < navigationHistory.length &&
          (!currentKey || getSubjectKey(navigationHistory[runtimeCursor]) === currentKey)
        ) {
          return runtimeCursor;
        }
        if (!currentKey) {
          return navigationHistory.length > 0 ? navigationHistory.length - 1 : null;
        }
        for (let i = navigationHistory.length - 1; i >= 0; i -= 1) {
          if (getSubjectKey(navigationHistory[i]) === currentKey) return i;
        }
        return navigationHistory.length > 0 ? navigationHistory.length - 1 : null;
      };
      const focusedAt = new Date().toISOString();
      const focusedEntry = toSubjectFocusHistoryEntry(normalizedSubject, focusedAt);
      const nextSubjectHistory = shouldRecordInHistory
        ? [
            ...existingHistory,
            focusedEntry,
          ].slice(-MAX_SUBJECT_HISTORY_SIZE)
        : existingHistory;
      let nextNavigationHistory = navigationHistory;
      let nextNavigationCursor: number | null =
        typeof runtimeCursor === 'number' && Number.isFinite(runtimeCursor) ? runtimeCursor : null;
      if (shouldRecordInHistory) {
        const navigationCursor = resolveNavigationCursor();
        const navigationBase =
          navigationCursor !== null &&
          navigationCursor >= 0 &&
          navigationCursor < navigationHistory.length - 1
            ? navigationHistory.slice(0, navigationCursor + 1)
            : navigationHistory;
        nextNavigationHistory = [...navigationBase, focusedEntry].slice(-MAX_SUBJECT_HISTORY_SIZE);
        nextNavigationCursor = Math.max(0, nextNavigationHistory.length - 1);
      }

      k.mergeEntityKernelState('ChatSession', sessionId, {
        activeSubject: normalizedSubject,
        subjectHistory: nextSubjectHistory,
      });
      if (shouldRecordInHistory) {
        k.patchEntityRuntimeState('ChatSession', sessionId, {
          subjectNavigationHistory: nextNavigationHistory,
          subjectHistoryCursor: nextNavigationCursor,
        });
      }
    } else {
      k.mergeEntityKernelState('ChatSession', sessionId, {
        activeSubject: null,
      });
      k.patchEntityRuntimeState('ChatSession', sessionId, {
        subjectHistoryCursor: null,
      });
    }

    if (normalizedSubject) {
      const subjectPayload: Record<string, unknown> = {
        entityType: normalizedSubject.entityType,
        entityId: normalizedSubject.entityId,
        ...(normalizedSubject.focusScope ? { focusScope: normalizedSubject.focusScope } : {}),
        ...(normalizedSubject.tableView ? { tableView: normalizedSubject.tableView } : {}),
        ...(normalizedSubject.focusActionType
          ? { focusActionType: normalizedSubject.focusActionType }
          : {}),
        // The campaign binding must ride the event: the subject is PERSISTED by folding these
        // events server-side, not by the in-memory merge above, so a field left out of this
        // payload exists exactly until the next refresh and then silently disappears.
        ...(normalizedSubject.campaignId ? { campaignId: normalizedSubject.campaignId } : {}),
        ...(shouldRecordInHistory ? {} : { recordInHistory: false }),
      };
      const isEntityFocus = isEntityFocusSubject(normalizedSubject);
      emitSessionTimelineEvent({
        sessionId,
        category: 'AGENTIC',
        eventType: 'SUBJECT_FOCUSED',
        targetEntityType: isEntityFocus ? normalizedSubject.entityType : null,
        targetEntityId: isEntityFocus ? normalizedSubject.entityId : null,
        payload: subjectPayload,
      });
    } else {
      emitSessionTimelineEvent({
        sessionId,
        category: 'AGENTIC',
        eventType: 'SUBJECT_CLEARED',
        payload: {},
      });
    }

    // Property has no equivalent: its branch existed only to pre-select a tab on the asset page,
    // and there is no asset page.
    if (normalizedSubject && isEntityFocusSubject(normalizedSubject) && normalizedSubject.entityType === 'Deal') {
      k.syncDealKernelStateFocus?.(normalizedSubject.entityId, normalizedSubject.focusActionType);
    }

    // A binding change ALSO rides the DTO autosave, on top of the timeline event. Focus changes
    // are normally event-only, but the sessions LIST endpoint serves the raw column without
    // folding events, and openSourceCampaign / the scoped-home lookup find bound conversations
    // through that list. A binding that lives only in the fold is invisible to them after a
    // reload, and the campaign would get a duplicate conversation minted on every open.
    const previousCampaignId =
      currentSubject && isEntityFocusSubject(currentSubject) ? currentSubject.campaignId ?? null : null;
    const nextCampaignId =
      normalizedSubject && isEntityFocusSubject(normalizedSubject) ? normalizedSubject.campaignId ?? null : null;
    if (previousCampaignId !== nextCampaignId) {
      markChatSessionDirty(sessionId);
      scheduleGlobalSessionSave(sessionId);
    }
  }, [emitSessionTimelineEvent, kernel, markChatSessionDirty, scheduleGlobalSessionSave]);

  const goToPreviousSubject = useCallback((sessionId: string): ActiveEntityFocus | null => {
    const k = kernel.current;
    if (!k) return null;

    const session = k.getInventory().ChatSession.registry[sessionId];
    if (!session?.activeSubject) return null;

    const runtime = k.readEntityRuntimeState('ChatSession', sessionId);
    const runtimeNavigationHistory = Array.isArray(runtime.subjectNavigationHistory)
      ? runtime.subjectNavigationHistory
      : [];
    const navigationHistory = runtimeNavigationHistory.length > 0
      ? runtimeNavigationHistory
      : (Array.isArray(session.subjectHistory) ? session.subjectHistory : []);
    const runtimeCursor =
      typeof runtime.subjectHistoryCursor === 'number' && Number.isFinite(runtime.subjectHistoryCursor)
        ? Math.floor(runtime.subjectHistoryCursor)
        : null;
    const { subject: resolvedPreviousSubject, historyIndex } = findPreviousValidSubject(
      session,
      k.getInventory(),
      { activeHistoryIndexHint: runtimeCursor, historyOverride: navigationHistory }
    );
    if (!resolvedPreviousSubject) {
      return null;
    }

    const resolvedWithAction = withRecoveredSourcingFocusAction(k, resolvedPreviousSubject);

    k.setActiveSubject(sessionId, resolvedWithAction, { recordInHistory: false });
    if (historyIndex >= 0) {
      k.patchEntityRuntimeState('ChatSession', sessionId, {
        subjectNavigationHistory: navigationHistory,
        subjectHistoryCursor: historyIndex,
      });
    }

    return resolvedWithAction;
  }, [kernel]);

  const canGoToPreviousSubject = useCallback((sessionId: string): boolean => {
    const k = kernel.current;
    if (!k) return false;

    const session = k.getInventory().ChatSession.registry[sessionId];
    if (!session?.activeSubject) return false;

    const runtime = k.readEntityRuntimeState('ChatSession', sessionId);
    const runtimeNavigationHistory = Array.isArray(runtime.subjectNavigationHistory)
      ? runtime.subjectNavigationHistory
      : [];
    const navigationHistory = runtimeNavigationHistory.length > 0
      ? runtimeNavigationHistory
      : (Array.isArray(session.subjectHistory) ? session.subjectHistory : []);
    const runtimeCursor =
      typeof runtime.subjectHistoryCursor === 'number' && Number.isFinite(runtime.subjectHistoryCursor)
        ? Math.floor(runtime.subjectHistoryCursor)
        : null;
    const { subject, activeIndex } = findPreviousValidSubject(session, k.getInventory(), {
      activeHistoryIndexHint: runtimeCursor,
      historyOverride: navigationHistory,
    });
    if (activeIndex === 0) return false;
    return Boolean(subject);
  }, [kernel]);

  const goToNextSubject = useCallback((sessionId: string): ActiveEntityFocus | null => {
    const k = kernel.current;
    if (!k) return null;

    const session = k.getInventory().ChatSession.registry[sessionId];
    if (!session?.activeSubject) return null;

    const runtime = k.readEntityRuntimeState('ChatSession', sessionId);
    const runtimeNavigationHistory = Array.isArray(runtime.subjectNavigationHistory)
      ? runtime.subjectNavigationHistory
      : [];
    const navigationHistory = runtimeNavigationHistory.length > 0
      ? runtimeNavigationHistory
      : (Array.isArray(session.subjectHistory) ? session.subjectHistory : []);
    const runtimeCursor =
      typeof runtime.subjectHistoryCursor === 'number' && Number.isFinite(runtime.subjectHistoryCursor)
        ? Math.floor(runtime.subjectHistoryCursor)
        : null;
    const { subject: resolvedNextSubject, historyIndex } = findNextValidSubject(
      session,
      k.getInventory(),
      { activeHistoryIndexHint: runtimeCursor, historyOverride: navigationHistory }
    );
    if (!resolvedNextSubject) {
      return null;
    }

    const resolvedWithAction = withRecoveredSourcingFocusAction(k, resolvedNextSubject);

    k.setActiveSubject(sessionId, resolvedWithAction, { recordInHistory: false });
    if (historyIndex >= 0) {
      k.patchEntityRuntimeState('ChatSession', sessionId, {
        subjectNavigationHistory: navigationHistory,
        subjectHistoryCursor: historyIndex,
      });
    }

    return resolvedWithAction;
  }, [kernel]);

  const canGoToNextSubject = useCallback((sessionId: string): boolean => {
    const k = kernel.current;
    if (!k) return false;

    const session = k.getInventory().ChatSession.registry[sessionId];
    if (!session?.activeSubject) return false;

    const runtime = k.readEntityRuntimeState('ChatSession', sessionId);
    const runtimeNavigationHistory = Array.isArray(runtime.subjectNavigationHistory)
      ? runtime.subjectNavigationHistory
      : [];
    const navigationHistory = runtimeNavigationHistory.length > 0
      ? runtimeNavigationHistory
      : (Array.isArray(session.subjectHistory) ? session.subjectHistory : []);
    const runtimeCursor =
      typeof runtime.subjectHistoryCursor === 'number' && Number.isFinite(runtime.subjectHistoryCursor)
        ? Math.floor(runtime.subjectHistoryCursor)
        : null;
    const { subject, activeIndex } = findNextValidSubject(session, k.getInventory(), {
      activeHistoryIndexHint: runtimeCursor,
      historyOverride: navigationHistory,
    });
    if (activeIndex >= navigationHistory.length - 1) return false;
    return Boolean(subject);
  }, [kernel]);

  const refreshGlobalActionFeed = useCallback(async (orgId?: string): Promise<void> => {
    const k = kernel.current;
    if (!k) return;

    const requestedOrgId = orgId ?? k.getUserPreferences?.().commanderOrganisationId ?? undefined;
    await k.fetchLeasingActions(requestedOrgId || undefined);
  }, [kernel]);

  const refreshEntityActionGroup = useCallback(async (
    entityType: EntityType,
    entityId: string,
    options?: { orgId?: string }
  ): Promise<LeasingActionItem[]> => {
    const k = kernel.current;
    if (!k || !entityType || !entityId) return [];

    const requestedOrgId = options?.orgId ?? k.getUserPreferences?.().commanderOrganisationId ?? undefined;

    try {
      const response = await axios.post<{
        recommendation: string;
        nextAction: LeasingActionItem | null;
        entityActions: LeasingActionItem[];
      }>(
        '/api/inventory/actions/next',
        {
          focusEntityType: entityType,
          focusEntityId: entityId,
          orgId: requestedOrgId,
        },
        { withCredentials: true }
      );

      const entityActions = Array.isArray(response.data?.entityActions) ? response.data.entityActions : [];

      k.setLeasingActions((previous) => {
        const retained = previous.filter(
          (action) => !(action.entityType === entityType && action.entityId === entityId)
        );
        return [...entityActions, ...retained];
      });

      return entityActions;
    } catch (error) {
      console.error('[useChat] Failed to refresh entity action group', { entityType, entityId, error });
      return [];
    }
  }, [kernel]);

  const resolveNextSubjectAction = useCallback(async (
    sessionId: string,
    resolvedSubjectOverride?: ActiveEntityFocus | null,
    options?: { applyToSession?: boolean }
  ): Promise<{ recommendation: string; nextAction: LeasingActionItem | null } | null> => {
    const k = kernel.current;
    if (!k) return null;

    const session = k.getInventory().ChatSession.registry[sessionId];
    if (!session) return null;

    const resolvedSubject = resolvedSubjectOverride ?? session.activeSubject ?? null;
    if (!resolvedSubject) return null;
    if (!isEntityFocusSubject(resolvedSubject)) return null;

    const applyToSession = options?.applyToSession ?? true;
    const requestedOrgId = k.getUserPreferences?.().commanderOrganisationId ?? undefined;

    try {
      const response = await axios.post<{
        recommendation: string;
        nextAction: LeasingActionItem | null;
      }>(
        '/api/inventory/actions/next',
        {
          resolvedSubject,
          orgId: requestedOrgId,
        },
        { withCredentials: true }
      );

      const nextAction = response.data?.nextAction ?? null;
      if (applyToSession) {
        if (nextAction?.type && nextAction.entityType && nextAction.entityId) {
          k.setActiveSubject(sessionId, {
            entityType: nextAction.entityType,
            entityId: nextAction.entityId,
            focusActionType: nextAction.type,
          });
        } else {
          k.setActiveSubject(sessionId, null);
        }
      }

      const refreshEntityType = nextAction?.entityType ?? resolvedSubject.entityType;
      const refreshEntityId = nextAction?.entityId ?? resolvedSubject.entityId;
      if (refreshEntityType && refreshEntityId) {
        await k.refreshEntityActionGroup(refreshEntityType, refreshEntityId, {
          orgId: requestedOrgId || undefined,
        });
      }
      return {
        recommendation: response.data?.recommendation ?? '',
        nextAction,
      };
    } catch (error) {
      console.error('[useChat] Failed to resolve next subject action', error);
      return null;
    }
  }, [kernel]);

  const handleSessionExit = useCallback(async (sessionId: string) => {
    if (!kernel.current) return;
    const k = kernel.current;
    
    const session = k.getInventory().ChatSession.registry[sessionId];
    if (!session) return; 

    if (session.isDirty) {
      k.persistChatSession(sessionId);
    }
  }, [kernel]);

  // Synchronous re-entry lock for the send button. The chatState-based guards below only turn
  // "working" once the turn has actually started, an async gap of hundreds of ms (session creation,
  // image uploads, a /factory fan-out that is not even an agent turn and never flips chatState). A
  // second click inside that gap used to pass every check and send again, spending twice. A ref
  // flips synchronously, before the first await, so the second click returns before it can act.
  const submitInFlightRef = useRef(false);
  const submitChatPrompt = useCallback(async (options?: ChatComposerSubmitOptions) => {
    const k = kernel.current;
    if (!k) return;

    const activeSessionId = k.getActiveChatSessionId();
    const runtime = activeSessionId ? k.readEntityRuntimeState('ChatSession', activeSessionId) : null;
    // Same liveness test as askAssistant's self-heal: a stranded lock (stream
    // killed mid-script) must not gate the composer for the life of the tab.
    const liveScriptId =
      runtime?.runningScriptId && isScriptExecuting(runtime.runningScriptId)
        ? runtime.runningScriptId
        : null;
    const isLoading = agentIsWorking(runtime?.chatState, liveScriptId);
    if (isLoading || k.getState().isQueuingChatMessage) return;

    // The lock covers every exit below (early returns, hero path, main path) through the finally.
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    // A new user turn is the ONLY thing that lifts a standing stop. runScript used to clear it on
    // every script start, so an auto-continued agent chain erased the user's own stop and kept
    // generating; clearing it here instead means the button holds until the user asks for
    // something new.
    if (activeSessionId) {
      k.patchEntityRuntimeState('ChatSession', activeSessionId, { isStopRequested: false });
      k.patchEntityRuntimeState('ChatSession', activeSessionId, { runConclusionAsked: false });
    }
    try {

    const livePrompt =
      typeof options?.promptOverride === 'string' && options.promptOverride.trim().length > 0
        ? options.promptOverride
        : typeof k.getState()?.chatPrompt === 'string'
          ? k.getState().chatPrompt
          : '';
    const trimmedPrompt = livePrompt.trim();
    const imageUrlOverrides = Array.isArray(options?.imageUrlOverrides)
      ? options.imageUrlOverrides.filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
      : [];
    const hasImages = k.getImagesInChatInput().length > 0 || imageUrlOverrides.length > 0;

    // In a Slides deck, "start the review" / "continue the review" IS the button, not an agent turn: a
    // review run inline in the chat had no cover page, progress or Stop / Continue (owner, 2026-10-09).
    if (activeSessionId && !hasImages && k.getInventory().ChatSession.registry[activeSessionId]?.creationModule === 'slides'
      && launchTypedSlidesReview(k, activeSessionId, trimmedPrompt)) {
      k.setChatPrompt('');
      return;
    }

    // Light the "agent working" glow the instant the user sends, while the Analyst is open, so it
    // shows immediately on ask, before a freshly-created session's chatState/script aligns to drive
    // agentThinking. The 6s linger bridges the send -> turn-start gap; agentThinking then holds it.
    if ((trimmedPrompt.length > 0 || hasImages) && useTablesStore.getState().isAnalystOpen) {
      useTablesStore.getState().markAgentActive();
    }

    // The hero composer is EXCLUSIVE to Master (the 4th module). Every send from the landing
    // composer starts a Master orchestration session; the caller's dropdown picks Master's
    // operating mode (Auto runs autonomously, the others pause for approval between steps).
    if (options?.hero) {
      if (trimmedPrompt.length === 0 && !hasImages) return;
      try {
        const heroSessionId = await k.createAndActivateSession({
          nameHint: trimmedPrompt || 'Master',
          creationScope: { module: 'master', entityType: null, entityId: null },
          collaborationMode: options.hero.collaborationMode,
        });
        if (heroSessionId) {
          await k.execute(livePrompt, imageUrlOverrides.length > 0 ? imageUrlOverrides : undefined, { agentMode: 'master', projectId: '' });
        }
      } catch (error) {
        console.error('[useChat] Failed to send prompt', error);
        k.handleError?.('Failed to send your message.');
      }
      return;
    }

    // A Studio project chat always talks to the Studio agent, and it has no Property to anchor.
    const chatContext = options?.chatContext ?? 'leasing';
    // Agent LLMs can run with no project bound; generative-only picks cannot. A project chat
    // always needs its project, whatever the model.
    const assistantModel = k.getUserPreferences()?.assistantModel ?? MAIN_TEXT_MODEL;
    const canExecuteWithoutProjectId = chatContext === 'leasing' && AGENT_LLMS.includes(assistantModel);
    const agentMode: AgentMode = chatContext === 'leasing' ? 'commander' : 'director';
    const selectedAssetId = chatContext === 'leasing' ? k.getState().selectedAssetId : null;
    const hasSubmission = trimmedPrompt.length > 0 || hasImages || Boolean(activeSessionId && k.getChatSessionMessages(activeSessionId).length);
    const complete = chatContext === 'leasing' && hasSubmission
      ? k.beginSourceAction?.({ action: 'chat', origin: 'human', data: { queued: false } }) : undefined;

    try {
      if (!activeSessionId) {
        if (trimmedPrompt.length > 0 || hasImages) {
          const initialSubject = selectedAssetId
            ? { entityType: 'Property' as const, entityId: selectedAssetId }
            : undefined;
          const newSessionId = await k.ensureSession({
            nameHint: trimmedPrompt,
            initialSubject,
          });
          if (newSessionId && selectedAssetId) {
            k.anchorEntity(newSessionId, 'Property', selectedAssetId, 8);
          }
          const executionProjectId = newSessionId
            ? k.getLastProjectId(newSessionId) ||
              resolveProjectIdFromChatSessionContext(
                k.getInventory().ChatSession.registry[newSessionId] ?? null
              )
            : null;
          if (!executionProjectId && !canExecuteWithoutProjectId) {
            throw new Error('Missing projectId for ensured session execution.');
          }
          await k.execute(livePrompt, imageUrlOverrides.length > 0 ? imageUrlOverrides : undefined, {
            agentMode,
            projectId: executionProjectId ?? '',
          });
        }
      } else {
        if (trimmedPrompt.length === 0 && !hasImages) {
          const messagesLength = k.getChatSessionMessages(activeSessionId).length;
          if (messagesLength > 0) {
            const continueProjectId =
              k.getLastProjectId(activeSessionId) ||
              resolveProjectIdFromChatSessionContext(
                k.getInventory().ChatSession.registry[activeSessionId] ?? null
              );
            if (!continueProjectId && !canExecuteWithoutProjectId) {
              throw new Error('Missing projectId for continue execution.');
            }
            await k.execute('continue', undefined, {
              agentMode,
              projectId: continueProjectId ?? '',
            });
          }
        } else {
          const targetSessionId = activeSessionId ?? k.getActiveChatSessionId();
          const executionProjectId = targetSessionId
            ? k.getLastProjectId(targetSessionId) ||
              resolveProjectIdFromChatSessionContext(
                k.getInventory().ChatSession.registry[targetSessionId] ?? null
              )
            : null;
          if (!executionProjectId && !canExecuteWithoutProjectId) {
            throw new Error('Missing projectId for session execution.');
          }
          await k.execute(livePrompt, imageUrlOverrides.length > 0 ? imageUrlOverrides : undefined, {
            agentMode,
            projectId: executionProjectId ?? '',
          });
        }
      }
    } catch (error) {
      complete?.(sourceActionFailure(error));
      console.error('[useChat] Failed to send prompt', error);
      k.handleError?.('Failed to send your message.');
    }

    } finally {
      submitInFlightRef.current = false;
    }
  }, [kernel]);

  const queueChatUserMessage = useCallback(async (options?: ChatComposerQueueOptions) => {
    const k = kernel.current;
    if (!k) return;

    const livePrompt = typeof k.getState()?.chatPrompt === 'string' ? k.getState().chatPrompt : '';
    const trimmedPrompt = livePrompt.trim();
    const images = k.getImagesInChatInput();
    const hasImages = images.length > 0;
    if (!hasImages && trimmedPrompt.length === 0) return;
    if (k.getState().isQueuingChatMessage) return;

    k.setState({ isQueuingChatMessage: true });
    k.logUserAction('Chat Queue Message Initiated');
    const complete = (options?.chatContext ?? 'leasing') === 'leasing'
      ? k.beginSourceAction?.({ action: 'chat', origin: 'human', data: { queued: true } }) : undefined;

    try {
      // A project chat rides the session the workspace already owns; only the leasing composer
      // opens one on the fly around the selected asset.
      const isLeasingContext = (options?.chatContext ?? 'leasing') === 'leasing';
      const selectedAssetId = isLeasingContext ? k.getState().selectedAssetId : null;
      let activeId = k.getActiveChatSessionId();
      let createdFromChipSelection = false;
      if (!activeId && isLeasingContext) {
        const initialSubject = selectedAssetId
          ? { entityType: 'Property' as const, entityId: selectedAssetId }
          : undefined;
        const ensuredId = await k.ensureSession({
          nameHint: trimmedPrompt,
          initialSubject,
        });
        activeId = ensuredId ?? null;
        createdFromChipSelection = Boolean(activeId);
      }
      if (!activeId && isLeasingContext) {
        throw new Error('Failed to start a global chat session.');
      }
      if (activeId && createdFromChipSelection && selectedAssetId) {
        k.anchorEntity(activeId, 'Property', selectedAssetId, 8);
      }

      const activeSessionIdForScope = k.getActiveChatSessionId();
      const activeSessionForScope = activeSessionIdForScope
        ? (k.getInventory().ChatSession.registry[activeSessionIdForScope] ?? null)
        : null;
      const projectIdFromRuntime = activeSessionIdForScope
        ? k.getLastProjectId(activeSessionIdForScope)
        : null;
      const scopedProjectId =
        (typeof projectIdFromRuntime === 'string' && projectIdFromRuntime.trim().length > 0
          ? projectIdFromRuntime.trim()
          : resolveProjectIdFromChatSessionContext(activeSessionForScope)) ?? null;
      const projectOwnerId = scopedProjectId
        ? k.getInventory().Project.registry[scopedProjectId]?.userId
        : undefined;
      let queuedPrompt: string;
      try { queuedPrompt = serializeFactoryFolderMentions(k, scopedProjectId ?? '', serializeListOutputMentions(k, scopedProjectId ?? '', livePrompt)); }
      catch (error) { k.handleError(error instanceof Error ? error.message : 'Select the output reference again.'); return; }
      const queuedUserId = k.getState().userContext?.dbUserId;

      const uploadPromises = images.map(async (lockableFile): Promise<ImageAttachment | null> => {
        const file = lockableFile.file;
        const isEdited = (file as any).originalFile && (file as any).cropPolygon;

        if (isEdited) {
          const originalFile = (file as any).originalFile as File;
          const polygons = (file as any).cropPolygon as { x: number; y: number }[][];

          const croppedFilename = `${Date.now()}_cropped.${getFileExtension(file.name) || 'bin'}`;
          const originalFilename = `${Date.now()}_original.${getFileExtension(originalFile.name) || 'bin'}`;

          const [croppedUrl, originalUrl] = await Promise.all([
            uploadFileToCloud(file, croppedFilename, file.type, undefined, projectOwnerId),
            uploadFileToCloud(originalFile, originalFilename, originalFile.type, undefined, projectOwnerId)
          ]);

          if (croppedUrl && originalUrl) {
            k.registerCloudAssets([croppedUrl, originalUrl], 'upload');
            return { url: croppedUrl, originalUrl, polygons };
          }
        } else {
          const filename = `${Date.now()}.${getFileExtension(file.name) || 'bin'}`;
          const url = await uploadFileToCloud(file, filename, file.type, undefined, projectOwnerId);
          if (url) {
            k.registerCloudAssets([url], 'upload');
            return { url };
          }
        }
        return null;
      });

      const uploaded = await Promise.all(uploadPromises);
      const imageAttachments = uploaded.filter((att): att is ImageAttachment => !!att);

      const currentQueuedProjectId = activeSessionIdForScope
        ? k.getLastProjectId(activeSessionIdForScope) || resolveProjectIdFromChatSessionContext(k.getInventory().ChatSession.registry[activeSessionIdForScope] ?? null)
        : null;
      if (k.getActiveChatSessionId() !== activeSessionIdForScope || k.getState().userContext?.dbUserId !== queuedUserId || currentQueuedProjectId !== scopedProjectId) {
        k.handleError('The conversation changed. Your draft was not queued; send it from its original project.');
        return;
      }
      // As a sent message: one about an attachment never leaves without it, it waits in the box, the failed file marked.
      if (imageAttachments.length < uploaded.length) {
        k.setImagesInChatInput(images.map((image, at) => ({ ...image, uploadFailed: !uploaded[at] || undefined })));
        k.handleError(CHAT_ATTACHMENT_UPLOAD_FAILED);
        return;
      }

      if (imageAttachments.length > 0 || trimmedPrompt.length > 0) {
        k.addUserMessage(queuedPrompt, imageAttachments);
        k.setChatPrompt('');
        k.setImagesInChatInput([]);
        k.logUserAction('Chat Message Queued', { has_text: trimmedPrompt.length > 0, image_count: imageAttachments.length });
        // SAY THAT IT IS WAITING. Sending while the agent works QUEUES the message, by design, and
        // until now nothing said so: the message appeared and then nothing happened. Measured on
        // 2026-08-16, a second "/extend" sat silent for 2min10s and was answered the instant the
        // running pass ended - from the user's seat that is indistinguishable from a lost message,
        // and the obvious reaction is to ask again, which spends a second time.
        //
        // Deliberately not `settled`: this line describes something still pending, and the strip
        // animating it is exactly right. It is replaced when the turn starts, because
        // startThinkingSequence keeps only settled lines.
        if (activeId) {
          // Ambient: it says something is pending without being "the agent working", which kept the
          // stop button up forever once the run ended without a script to clear it.
          k.setSessionStatus(
            activeId,
            'Waiting for the current run to finish before sending this',
            { replacesPrevious: false, ambient: true },
          );
          // Queuing used to rely on the run continuing on its own and reading this message from the
          // history; a run that ended any other way (its closing conclusion, a terminal command, a
          // plain answer) never sent it (owner, 2026-10-15). flushQueuedChatMessage sends it once idle.
          const queued = k.getChatSessionMessages(activeId).findLast((m) => m.role === 'user');
          if (queued?.timestamp) {
            k.patchEntityRuntimeState('ChatSession', activeId, {
              queuedUserMessage: { timestamp: queued.timestamp, prompt: queuedPrompt, imageUrls: imageAttachments.map((a) => a.url),
                agentMode: (options?.chatContext ?? 'leasing') === 'leasing' ? 'commander' : 'director' },
            });
          }
        }
      }
    } catch (error) {
      complete?.(sourceActionFailure(error));
      console.error("Error queuing message with image uploads:", error);
      k.logUserAction('Chat Queue Message Failed', { error: (error as Error).message });
      k.handleError("Failed to upload images for your message.");
    } finally {
      k.setState({ isQueuingChatMessage: false });
    }
  }, [kernel]);

  const flushQueuedChatMessage = useCallback(async (sessionId: string) => {
    const k = kernel.current;
    if (!k) return;
    const runtime = k.readEntityRuntimeState('ChatSession', sessionId);
    const queued = runtime.queuedUserMessage;
    if (!queued || k.getActiveChatSessionId() !== sessionId) return;
    const live = typeof runtime.runningScriptId === 'string' && runtime.runningScriptId.trim() && isScriptExecuting(runtime.runningScriptId.trim());
    if (live || agentIsWorking(runtime.chatState, null) || (runtime.abortController && !runtime.abortController.signal.aborted)) return;
    // Still set means no turn has started since it was queued (a turn takes it: useAgents takeQueuedMessage).
    // An answer stamped after it proves nothing: the reply being written when it was queued ends later.
    const stillThere = k.getChatSessionMessages(sessionId).some((m) => m.role === 'user' && m.timestamp === queued.timestamp);
    k.patchEntityRuntimeState('ChatSession', sessionId, {
      queuedUserMessage: null,
      chatState: (runtime.chatState ?? []).filter((entry) => entry.settled === true),
    });
    if (!stillThere) return;
    k.logUserAction('Chat Queued Message Sent', { has_text: queued.prompt.trim().length > 0, image_count: queued.imageUrls.length });
    const creationModule = k.getInventory().ChatSession.registry[sessionId]?.creationModule;
    k.startThinkingSequence?.(sessionId);
    await k.askAssistant(sessionId, queued.prompt, queued.imageUrls, undefined, {
      skipUserMessage: true, queuedByUser: true,
      // As execute does for a typed message: a Slides or Plan conversation keeps its own agent.
      agentMode: creationModule === 'slides' ? 'slides' : creationModule === 'pulse' ? 'pulse' : queued.agentMode,
    });
  }, [kernel]);

  /**
   * Re-attaches an image already present in the chat history to the composer.
   * An edited image (cropped, with polygons) carries its original file and crop polygon
   * along, so the composer keeps the lineage; a plain image is deduplicated on
   * name/size/type against what the composer already holds.
   */
  const reAddChatImageToComposer = useCallback(async (message: Message, imageIndex: number) => {
    if (!kernel.current) return;

    const imageAttachment = message.images?.[imageIndex];
    const imageUrl = imageAttachment?.url || message.imageUrls?.[imageIndex];

    if (!imageUrl) return;

    if (imageAttachment && imageAttachment.originalUrl && imageAttachment.polygons) {
      try {
        const [croppedResponse, originalResponse] = await Promise.all([
          fetch(imageUrl),
          fetch(imageAttachment.originalUrl)
        ]);

        if (!croppedResponse.ok || !originalResponse.ok) {
          throw new Error('Failed to fetch image data');
        }

        const [croppedBlob, originalBlob] = await Promise.all([
          croppedResponse.blob(),
          originalResponse.blob()
        ]);

        const croppedFileName = imageUrl.split('/').pop()?.split('?')[0] || 'cropped.png';
        const originalFileName = imageAttachment.originalUrl.split('/').pop()?.split('?')[0] || 'original.png';

        const croppedFile = new File([croppedBlob], croppedFileName, { type: croppedBlob.type });
        const originalFile = new File([originalBlob], originalFileName, { type: originalBlob.type });

        (croppedFile as any).originalFile = originalFile;
        (croppedFile as any).cropPolygon = imageAttachment.polygons;

        const lockableFile: LockableFile = { id: uuidv4(), file: croppedFile, locked: false };
        kernel.current.setImagesInChatInput(prev => [...prev, lockableFile]);
        kernel.current?.logUserAction('Chat Edited Image Re-added', { source: 'chat_history_click', url: imageUrl });

      } catch (error) {
        console.error('Error re-adding edited image from chat:', error);
        kernel.current.handleError?.('Could not re-add edited image.');
      }
    } else {
      try {
        const response = await fetch(imageUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
        }
        const blob = await response.blob();

        const fileName = imageUrl.split('/').pop()?.split('?')[0] || 'pasted-image.png';

        const file = new File([blob], fileName, { type: blob.type || 'image/png' });

        kernel.current.setImagesInChatInput(prevFiles => {
          const isDuplicate = prevFiles.some(
            existingFile =>
              existingFile.file.name === file.name &&
              existingFile.file.size === file.size &&
              existingFile.file.type === file.type
          );

          if (isDuplicate) {
            return prevFiles;
          }

          kernel.current?.logUserAction('Chat Image Re-added', { source: 'chat_history_click', url: imageUrl });
          const lockableFile: LockableFile = { id: uuidv4(), file: file, locked: false };
          return [...prevFiles, lockableFile];
        });
      } catch (error) {
        console.error('Error re-adding image from chat:', error);
        if (kernel.current.handleError) {
          kernel.current.handleError('Could not add image to input.');
        }
      }
    }
  }, [kernel]);

  /** Saves a chat message's text as a reusable prompt in the user's prompt library. */
  const savePromptToLibrary = useCallback(async (prompt: string) => {
    try {
      const response = await fetch('/api/prompt-library', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save', prompt }),
      });
      if (!response.ok) {
        const messageText = await response.text();
        throw new Error(messageText || 'Failed to save prompt.');
      }
    } catch (error) {
      console.error('[PromptLibrary] Failed to save prompt from chat', error);
    }
  }, []);

  /** Reads one page of the prompt library. Throws with the server message so the caller can display it. */
  const listPromptLibrary = useCallback(async (params: PromptLibraryListParams): Promise<PromptLibraryListResponse> => {
    const query = new URLSearchParams();
    query.set('limit', String(params.limit));
    query.set('offset', String(params.offset));
    if (params.ownerId) {
      query.set('ownerId', params.ownerId);
    }
    if (params.search && params.search.length > 0) {
      query.set('search', params.search);
    }
    const response = await fetch(`/api/prompt-library?${query.toString()}`);
    if (!response.ok) {
      const messageText = await response.text();
      throw new Error(messageText || 'Failed to load prompt library.');
    }
    return await response.json();
  }, []);

  /** Counts one reuse of a saved prompt. Throws so the caller only credits the count on success. */
  const incrementPromptLibraryUsage = useCallback(async (promptId: string) => {
    const response = await fetch('/api/prompt-library', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'increment', id: promptId }),
    });
    if (!response.ok) {
      const messageText = await response.text();
      throw new Error(messageText || 'Failed to update prompt count.');
    }
  }, []);

  /** Deletes a saved prompt. Throws so the caller only drops the row on success. */
  const removePromptFromLibrary = useCallback(async (promptId: string) => {
    const response = await fetch('/api/prompt-library', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'remove', id: promptId }),
    });
    if (!response.ok) {
      const messageText = await response.text();
      throw new Error(messageText || 'Failed to remove prompt.');
    }
  }, []);

  const openChatComposerAttachMenu = useCallback((options: ChatComposerAttachMenuOptions) => {
    const k = kernel.current;
    const anchorElement = options.anchorElement;
    if (!k || !anchorElement) return;

    const rect = anchorElement.getBoundingClientRect();
    const activeChatSessionId = k.getActiveChatSessionId();
    const activeSession = activeChatSessionId
      ? (k.getInventory().ChatSession.registry[activeChatSessionId] ?? null)
      : null;
    const projectIdFromRuntime = activeChatSessionId ? k.getLastProjectId(activeChatSessionId) : null;
    const scopedProjectId =
      (typeof projectIdFromRuntime === 'string' && projectIdFromRuntime.trim().length > 0
        ? projectIdFromRuntime.trim()
        : resolveProjectIdFromChatSessionContext(activeSession)) ?? null;

    const decoratedActions: QuickMenuAction[] = [];
    // The Studio attach actions (Generation Area / layers) below assume a Studio project. For a
    // Slides deck (or any non-Studio project) the workspace/layer lookups can throw; that used to
    // bubble out of the handler and left the "+" menu unable to open at all, so images could not
    // be attached in the Slides chat. Isolate it so the always-available "Add photos & files"
    // action is offered regardless of whatever the Studio-specific block does.
    if (scopedProjectId) {
      try {
        const inventory = k.getInventory();
        const project = inventory.Project.registry[scopedProjectId];
        const workspaceIds = Array.isArray(project?.workspaceIds)
          ? project.workspaceIds.filter(
              (workspaceId): workspaceId is string =>
                typeof workspaceId === 'string' && workspaceId.trim().length > 0
            )
          : [];

        const layerIds = new Set<string>();
        workspaceIds.forEach((workspaceId) => {
          const workspace = inventory.StudioWorkspace.registry[workspaceId];
          const ids = Array.isArray(workspace?.layerIds)
            ? workspace.layerIds.filter(
                (layerId): layerId is string =>
                  typeof layerId === 'string' && layerId.trim().length > 0
              )
            : [];
          ids.forEach((layerId) => layerIds.add(layerId));
        });
        const layerRegistry = k.getLayerRegistry();
        const currentLayers = Array.from(layerIds)
          .map((layerId) => layerRegistry[layerId])
          .filter((layer): layer is NonNullable<typeof layer> => Boolean(layer));

        const deps: ContextMenuActionDependencies = {
          workspaceId: k.getProjectUIWorkspaceId(scopedProjectId),
          onImagesChange: k.setImagesInChatInput,
          imageFiles: k.getImagesInChatInput(),
          layers: currentLayers,
          selectedLayersId: getProjectSelectedLayersId(k, scopedProjectId),
          multiple: true,
          kernel,
          projectType: (project as { projectType?: string } | undefined)?.projectType,
        };
        const actionHintByLabel: Record<string, string> = {
          'Add Generation Area': 'Attach the frame you are working on',
          'Add active output': 'Attach the latest output',
          'Add selected layers': 'Attach the layers selected on the canvas',
          'Clear All': 'Remove every attachment',
        };
        const actionIconByLabel: Record<string, ReactElement> = {
          'Add Generation Area': createElement(ImagePlus, MENU_ICON_PROPS),
          'Add active output': createElement(ImagePlus, MENU_ICON_PROPS),
          'Add selected layers': createElement(Layers, MENU_ICON_PROPS),
          'Clear All': createElement(Trash2, MENU_ICON_PROPS),
        };
        decoratedActions.push(
          ...getGenericImageInputContextMenuActions(deps).map((action) => ({
            ...action,
            icon: action.icon ?? actionIconByLabel[action.label],
            hint: action.hint ?? actionHintByLabel[action.label],
          }))
        );
      } catch (err) {
        console.warn('[useChat] Studio attach actions unavailable for this project; offering file import only.', err);
      }
    }

    const importFileAction: QuickMenuAction = {
      label: options.importLabel,
      onClick: () => {
        options.onImportFiles();
        kernel.current?.logUserAction(options.importLogEvent);
      },
      icon: createElement(Paperclip, MENU_ICON_PROPS),
      hint: 'Upload from computer',
    };

    // Mode control removed per product, collaboration mode is no longer surfaced in the + menu.
    // The Agent (LLM) selector was removed too: the assistant LLM is a backend system choice made
    // by Fater, never a user-facing pick. Users choose the GENERATIVE models via Models control.
    const finalActions = [importFileAction, ...decoratedActions];

    // Anchor the menu to the composer CARD's top (not the + button inside it), with a small gap,
    // so the menu floats fully above the card, otherwise the card's lighter edge peeks out around
    // the menu's rounded corner. QuickMenu bottom-anchors via translateY(-100%) below mid-screen.
    const cardRect = anchorElement.closest('[data-composer-card]')?.getBoundingClientRect();
    // ChatGPT's + menu: as wide as the chat box, its left edge on the box's (owner, 2026-09-28).
    k.setMenuData({
      position: { x: cardRect?.left ?? rect.left, y: (cardRect?.top ?? rect.top) - 8 },
      actions: finalActions,
      skin: 'composer',
      width: cardRect?.width,
    });
    if (options.menuLogEvent) {
      k.logUserAction(options.menuLogEvent);
    }
  }, [kernel]);

  return useMemo(() => ({
    createAndActivateChatSession,
    renameChatSession,
    deleteChatSession,
    flushChatSession,
    addUserMessage,
    recordAgentTelemetry,
    persistVisibleMessage,
    addPersistedAssistantMessage,
    maybeAutoTitleSession,
    updateActiveSessionMessages,
    deleteMessage,
    openChatMessageMenu,
    showChatAttachmentToAgent,
    downloadChatImage,
    deleteChatSessionSilently,
    createAndActivateSession,
    ensureSession,
    activateChatSession,
    persistChatSession,
    markChatSessionDirty,
    setSessionCollaborationMode,
    anchorEntity,
    unanchorEntity,
    clearSessionFocus,
    setSessionTableFocusPresentationMode,
    setActiveSubject,
    goToPreviousSubject,
    canGoToPreviousSubject,
    goToNextSubject,
    canGoToNextSubject,
    refreshGlobalActionFeed,
    refreshEntityActionGroup,
    getChatSessionMessages,
    syncChatSessionMessagesFromTimeline: syncSessionMessagesFromTimelineRegistry,
    resolveNextSubjectAction,
    handleSessionExit,
    clearActiveChatSession,
    startThinkingSequence,
    setSessionStatus,
    getLastProjectId,
    resolveActiveSessionProjectId,
    requestScriptStop,
    submitChatPrompt,
    queueChatUserMessage,
    flushQueuedChatMessage,
    openChatComposerAttachMenu,
    reAddChatImageToComposer,
    savePromptToLibrary,
    listPromptLibrary,
    incrementPromptLibraryUsage,
    removePromptFromLibrary
  }), [
    createAndActivateChatSession,
    renameChatSession,
    deleteChatSession,
    flushChatSession,
    addUserMessage,
    recordAgentTelemetry,
    persistVisibleMessage,
    addPersistedAssistantMessage,
    maybeAutoTitleSession,
    updateActiveSessionMessages,
    deleteMessage,
    openChatMessageMenu,
    showChatAttachmentToAgent,
    deleteChatSessionSilently,
    createAndActivateSession,
    ensureSession,
    activateChatSession,
    persistChatSession,
    markChatSessionDirty,
    setSessionCollaborationMode,
    anchorEntity,
    unanchorEntity,
    clearSessionFocus,
    setSessionTableFocusPresentationMode,
    setActiveSubject,
    goToPreviousSubject,
    canGoToPreviousSubject,
    goToNextSubject,
    canGoToNextSubject,
    refreshGlobalActionFeed,
    refreshEntityActionGroup,
    getChatSessionMessages,
    syncSessionMessagesFromTimelineRegistry,
    resolveNextSubjectAction,
    handleSessionExit,
    clearActiveChatSession,
    startThinkingSequence,
    setSessionStatus,
    getLastProjectId,
    resolveActiveSessionProjectId,
    requestScriptStop,
    submitChatPrompt,
    queueChatUserMessage,
    flushQueuedChatMessage,
    openChatComposerAttachMenu,
    reAddChatImageToComposer,
    savePromptToLibrary,
    listPromptLibrary,
    incrementPromptLibraryUsage,
    removePromptFromLibrary
  ]);
};
